import React, { useCallback, useState, useContext, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Alert,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarsIcon from '@mui/icons-material/Stars';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SendIcon from "@mui/icons-material/Send";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import api from '../adapters/api';
import { ownUnitPrice } from '../utils/unitPrice';
import { computeCheckoutTotals, shippingForTotal, sumProducts } from '../utils/checkoutTotals';
import { canEditManualDiscount } from '../utils/discountPermission';

import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { FormHelperText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";
import "../App.css";

import thDate from "dayjs/locale/th";
import yearAdapter from "../adapters/yearAdapter";
import {
  DatePicker,
  LocalizationProvider
} from "@mui/x-date-pickers";
import dayjs from "dayjs";

import QuotationDocument from '../components/formDocument/QuotationDocument';
import { usePreviewPDF, showQuotationDone } from "../hooks/usePreviewPDF";

import NewCustomerDialog from '../components/dialog/NewCustomerDialog';
import NewDeliveryLocationDialog from '../components/dialog/NewDeliveryLocationDialog';
import InlineCustomerNote from '../components/insights/InlineCustomerNote';

const generateUUID = () =>
  crypto.randomUUID?.() ??
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });

function formatNumber(number) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function renderStockTooltip(row) {
  const dotColor = row.isInStock == null
    ? '#9e9e9e'
    : row.isLocalShort ? '#ed6c02'
    : row.isInStock ? '#66bb6a' : '#ef5350';
  const statusLabel = row.isInStock == null
    ? 'กำลังตรวจสต็อค...'
    : row.isLocalShort ? 'สต็อคสาขาไม่เพียงพอ'
    : row.isInStock ? 'มีสินค้า' : 'หมดสต็อค';

  const div = <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', my: 0.3 }} />;
  const R = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>{value}</Typography>
    </Box>
  );

  const activeBranches = (row.branches ?? []).filter(b => b.qty > 0);
  const hasPending = row.totalAccruedIn > 0 || row.totalAccruedOut > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#fff', lineHeight: 1 }}>{statusLabel}</Typography>
      </Box>

      {row.isInStock != null && <>
        {div}
        <R label="รวมทั้งหมด" value={formatNumber(row.totalStockQty)} />

        {activeBranches.length > 0 && <>
          {div}
          {activeBranches.map((b, i) => <R key={i} label={b.name} value={formatNumber(b.qty)} />)}
        </>}

        {hasPending && <>
          {div}
          {row.totalAccruedIn > 0 && <R label="ค้างรับ" value={formatNumber(row.totalAccruedIn)} />}
          {row.totalAccruedOut > 0 && <R label="ค้างส่ง" value={formatNumber(row.totalAccruedOut)} />}
        </>}
      </>}
    </Box>
  );
}

// erp_user.name_2 เก็บ "ชื่อผู้ใช้" (user_name) ไม่ใช่ user_code — บางคนสองค่านี้ไม่เหมือนกัน (เช่น dddd/duen)
// หมายเหตุสถานที่ส่ง — ถ้าที่อยู่มีคำว่า "ส่ง" อยู่แล้ว ไม่ต้องเติมซ้ำ
const deliveryRemark = (addr) => {
  const a = String(addr ?? '').trim();
  return a.includes('ส่ง') ? a : `ส่ง ${a}`;
};

const norm = (v) => String(v ?? '').trim().toLowerCase();
// ช่องกรอกโค้ดโปรโมชันในหน้า Checkout — ยังไม่เปิดใช้งาน (โปรโมชันอัตโนมัติยังทำงานตามปกติ)
const SHOW_PROMO_CODE = false;

const findCurrentSalesperson = (list, user) => {
  if (!user) return undefined;
  return list.find(s => norm(s.name_2) && norm(s.name_2) === norm(user.user_name))
      ?? list.find(s => norm(s.name_2) && norm(s.name_2) === norm(user.user_code));
};

export default function CartGrid() {
  const previewPDF = usePreviewPDF();
  const navigate = useNavigate();

  // Contexts
  const { cart, cartGuid, setCartGuid, deleteItemFromCart, fetchCart, updateDetailsItemInCart, setPriceLevel, deleteCart, resetCartState, currentOrderNo, createCartIfNotExist, priceLevel, customerId: ctxCustomerId, customerName: ctxCustomerName, lockInfo, setLockInfo } = useContext(CartContext);
  const { currentUser } = useContext(AuthContext);

  const isMobileLayout = useMediaQuery('(max-width:899px)');
  const [mobileTab, setMobileTab] = useState(0);

  // Snackbar state
  const [snackbar, setSnackbar] = useState(null);
  const handleCloseSnackbar = () => setSnackbar(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cart & Grid state
  const [updatedRows, setUpdatedRows] = useState([]);
  const [originalPrices, setOriginalPrices] = useState({});
  const [manualPriceEdits, setManualPriceEdits] = useState(new Set());

  // Restore manualPriceEdits from sessionStorage when cartGuid is available
  useEffect(() => {
    if (!cartGuid) return;
    try {
      const stored = sessionStorage.getItem(`mpe_${cartGuid}`);
      if (stored) setManualPriceEdits(new Set(JSON.parse(stored)));
    } catch {}
  }, [cartGuid]);

  // Persist manualPriceEdits to sessionStorage
  useEffect(() => {
    if (!cartGuid) return;
    sessionStorage.setItem(`mpe_${cartGuid}`, JSON.stringify([...manualPriceEdits]));
  }, [manualPriceEdits, cartGuid]);
  const [totalWeight, setTotalWeight] = useState(null);
  const [maxLength, setMaxLength] = useState(null);
  const [discount, setDiscount] = useState(null);
  const savedDiscountRef = useRef(null); // ส่วนลดที่ server บันทึกแล้ว (คืนค่าเมื่อบันทึกไม่ผ่าน)

  // Promotion state
  const [appliedPromos, setAppliedPromos] = useState([]);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Loyalty state
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [pointsToEarn, setPointsToEarn] = useState(0);
  const [redeemInput, setRedeemInput] = useState('');
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyPointsRedeemed, setLoyaltyPointsRedeemed] = useState(0);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);

  // Idempotency key — stable per checkout session, reset after successful QT (F7)
  const checkoutRequestId = useRef(generateUUID());

  // ป้องกัน listApplied (ไม่มี debounce) ทับผล evaluate (มี debounce) เมื่อ response ช้า
  const hasEvaluatedRef = useRef(false);

  // Compact list editing state
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [editingCell, setEditingCell] = useState(null); // { roworder, field }
  const [editValue, setEditValue] = useState('');
  const [dragOverId, setDragOverId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [stockTipId, setStockTipId] = useState(null); // roworder whose stock tooltip is open (tap-to-open on touch)
  const dragItemRef = useRef(null);
  const dragOverRef = useRef(null);
  const scrollBoxRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchAbortRef = useRef(null);

  // Customer & Sales state
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState('');
  const [inputValue, setInputValue] = useState("");
  const [salespersons, setSalespersons] = useState([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState("");
  const [branchList, setBranchList] = useState([]);
  const [creditDay, setCreditDay] = useState(0);
  const [email, setEmail] = useState('');

  // Pick up
  const [pickupBranch, setPickupBranch] = useState("");

  // Invoice & bill state
  const [invoiceDate, setInvoiceDate] = useState(null);
  const [arType, setArType] = useState('');
  const [branch, setBranch] = useState('');
  const [priceType, setPriceType] = useState('');
  const [remark, setRemark] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [draftPriority, setDraftPriority] = useState('waiting');
  const [totalSumAmount, setTotalSumAmount] = useState(null);
  const priceLevelMap = { 0: "สด", 1: "7%", 2: "10%", 3: "12%", 4: "15%" };
  const [taxType, setTaxType] = useState('');

  // Shipping state
  const [shippingType, setShippingType] = useState('');
  const [shippingCost, setShippingCost] = useState(null);
  const [addShipping, setAddShipping] = useState('');
  const [shippingInfo, setShippingInfo] = useState('');
  const [isShippingEstimated, setIsShippingEstimated] = useState(false); // ไม่มีพิกัด → ประมาณจากกลางตำบล
  const [isShippingCostManual, setIsShippingCostManual] = useState(false);
  const [latestInvoiceShippingCost, setLatestInvoiceShippingCost] = useState(null); // null = ไม่มีประวัติส่งที่อยู่นี้
  const [latestInvoiceDate, setLatestInvoiceDate] = useState('');
  const [latestInvoiceRemark, setLatestInvoiceRemark] = useState('');

  // Delivery locations
  const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(null);

  // Unit options
  const [unitOptions, setUnitOptions] = useState({});

  // dialog
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);

  const isEditing   = Boolean(currentOrderNo);
  const isDraftEdit = isEditing && currentOrderNo.startsWith("DRAFT-");
  const isQTEdit    = isEditing && !isDraftEdit;

  const isCreditGroup = useMemo(
    () => salespersons.find(s => s.code === selectedSalesperson)
            ?.groups?.some(g => g.group_code === 'แผนกขายเครดิต') ?? false,
    [selectedSalesperson, salespersons]
  );

  // เลือกระดับราคาเองได้เฉพาะ admin (level >= 2) หรือแผนกขายเครดิต / แผนกธุรการเซลล์ (แก้ราคารายสินค้าได้ตามปกติ)
  const canChangePriceLevel = (currentUser?.user_level ?? 0) >= 2 || (currentUser?.dept?.some(d => ['แผนกขายเครดิต', 'แผนกธุรการเซลล์'].includes(d)) ?? false);
  // ส่วนลดด้วยมือ — กฎเดียวกับ server (utils/discountPermission.js) · ไม่เกี่ยวกับส่วนลดโปรโมชัน/แต้ม
  const canEditDiscount = canEditManualDiscount(currentUser);

  // บิลเครดิต: ธุรการเซลล์เลือกพนักงานขายได้เฉพาะเมื่อเอกสาร/ลูกค้าไม่มีชื่อเซลล์มาตั้งแต่แรก
  // (จำค่าตอนโหลด/เลือกลูกค้า ไม่ใช่ค่าปัจจุบัน — เลือกผิดแล้วยังแก้ได้) และเลือกได้เฉพาะคนในแผนกขายเครดิต
  // (ถ้าเลือกคนนอกแผนก effect ด้านล่างจะรีเซ็ตบิลเป็นเงินสดเอง)
  const [saleCodeWasEmpty, setSaleCodeWasEmpty] = useState(false);
  const isSalesAdmin = currentUser?.dept?.includes('แผนกธุรการเซลล์') ?? false;
  const isCreditBill = arType === "เครดิต";
  const canChangeSalesperson = !isCreditBill || (isSalesAdmin && saleCodeWasEmpty);

  // ผู้ใช้ปัจจุบันอยู่ตำแหน่งแรกเสมอ ไม่มี duplicate
  const sortedSalespersons = useMemo(() => {
    if (!salespersons.length || !currentUser?.user_code) return salespersons;
    const me = findCurrentSalesperson(salespersons, currentUser);
    if (!me) return salespersons;
    return [me, ...salespersons.filter(s => s.code !== me.code)];
  }, [salespersons, currentUser]);

  const promosByItemCode = useMemo(() => {
    const map = {};
    for (const p of appliedPromos) {
      if (!p.matched_item_codes) continue;
      for (const entry of p.matched_item_codes) {
        const code = entry.item_code;
        if (!map[code]) map[code] = [];
        map[code].push(p);
      }
    }
    return map;
  }, [appliedPromos]);

  const discountByItemCode = useMemo(() => {
    const map = {};
    for (const p of appliedPromos) {
      if (!p.matched_item_codes) continue;
      for (const entry of p.matched_item_codes) {
        const code = entry.item_code;
        if (!map[code]) map[code] = 0;
        map[code] += entry.line_discount || 0;
      }
    }
    return map;
  }, [appliedPromos]);

  const manualEditItemCodes = useMemo(() => {
    const codes = new Set();
    for (const row of updatedRows) {
      if (manualPriceEdits.has(row.roworder)) codes.add(row.item_code);
    }
    return codes;
  }, [updatedRows, manualPriceEdits]);

  const effectivePromoDiscount = useMemo(() => {
    let excluded = 0;
    for (const code of manualEditItemCodes) {
      excluded += discountByItemCode[code] || 0;
    }
    return Math.max(0, promoDiscount - excluded);
  }, [promoDiscount, manualEditItemCodes, discountByItemCode]);

  // Same formula as effectivePromoDiscount, but computable synchronously from
  // a just-fetched `applied` array (state updates from setAppliedPromos/
  // setPromoDiscount aren't visible until the next render, so this is used
  // right before addQT instead of the stale memoized value).
  const computeEffectivePromoDiscount = (applied) => {
    const byItem = {};
    for (const p of applied) {
      if (!p.matched_item_codes) continue;
      for (const entry of p.matched_item_codes) {
        byItem[entry.item_code] = (byItem[entry.item_code] || 0) + (entry.line_discount || 0);
      }
    }
    const total = applied.filter(p => !p.is_free_gift)
      .reduce((s, p) => s + Number(p.discount_amount || 0), 0);
    let excluded = 0;
    for (const code of manualEditItemCodes) excluded += byItem[code] || 0;
    return Math.max(0, total - excluded);
  };

  // --- API calls & Effects ---

  // ฟังก์ชันอัปเดตฟิลด์ header ของ cart ทันทีเมื่อมีการเปลี่ยนค่าใน input
  async function updateCartHeaderField(fieldName, fieldValue) {
    try {
      await api.post("/api/updateCartHeaderField", {
        fieldName,
        fieldValue,
        creator_code: currentUser?.user_code,
      });
    } catch (error) {
      console.error("Error updating cart header field:", error);
    }
  }

  // ยอดที่ใช้เช็คเงื่อนไขส่งฟรี: ยอดสินค้า − ส่วนลด (ไม่รวมค่าส่ง) — ใช้สูตรเดียวกันทุกจุดที่คำนวณค่าส่ง
  const getShippingBaseAmount = () =>
    cart.reduce((acc, item) => acc + Number(item.qty) * Number(item.price), 0) - Number(discount || 0);

  // ฟังก์ชันคำนวณค่าขนส่งโดยเรียก API จาก backend
  const calculateShippingCost = async (
    cust_code = customerId,
    location = selectedLocation,
    total_sum_amount = getShippingBaseAmount(),
    weight = totalWeight,
    maxLen = maxLength
  ) => {
    try {
      const response = await api.get("/api/calculateShipping", {
        params: {
          cust_code,
          totalWeight: weight,
          total_sum_amount,
          delivery_location_id: location,
          max_length: maxLen,
        },
      });
      const cost = Number(response.data.shippingCost) || 0;
      const shippingInfo = response.data.shippingInfo || "";
      const invoiceRemark = response.data.latestInvoice?.remark || "";
      setShippingInfo(shippingInfo);
      setIsShippingEstimated(Boolean(response.data.isEstimated));
      if (response.data.latestInvoice) {
        const { formattedPrice, formattedDate, remark } = response.data.latestInvoice;
        setLatestInvoiceShippingCost(Number(formattedPrice) || 0);
        setLatestInvoiceDate(formattedDate || "");
        setLatestInvoiceRemark(remark || '');
      } else {
        setLatestInvoiceShippingCost(null);
        setLatestInvoiceDate("");
        setLatestInvoiceRemark('');
      }
      return { cost, shippingInfo, invoiceRemark };
    } catch (error) {
      console.error("Error calculating shipping cost:", error);
      setShippingInfo("");
      setIsShippingEstimated(false);
      setLatestInvoiceShippingCost(null);
      setLatestInvoiceDate("");
      setLatestInvoiceRemark('');
      return { cost: 0, shippingInfo: "", invoiceRemark: "" };
    }
  };

  

  useEffect(() => {
  if (!currentUser?.user_code) return;
  // ถ้า cartGuid ยังเป็น null ให้สร้างใหม่
  if (!cartGuid) {
    createCartIfNotExist().then(() => {
      fetchCart();
    });
  }
}, [currentUser, cartGuid]);

  const didFetchCart = useRef(false);
  // ดึงข้อมูลตะกร้าแค่ครั้งเดียว เมื่อเปิดหน้าที่มี currentOrderNo (แก้ไข Draft/Order)
  useEffect(() => {
    if (currentOrderNo && !didFetchCart.current) {
      fetchCart();
      didFetchCart.current = true;
    }
  }, [currentOrderNo, fetchCart]);

  const lockInfoRef = useRef(null);
  useEffect(() => { lockInfoRef.current = lockInfo; }, [lockInfo]);

  useEffect(() => {
    let isUnloading = false;
    const handleBeforeUnload = () => {
      isUnloading = true;
      const li = lockInfoRef.current;
      if (!li) return;
      const blob = new Blob(
        [JSON.stringify({ doc_no: li.doc_no, guid: li.guid })],
        { type: 'application/json' }
      );
      navigator.sendBeacon('/api/unlockBill', blob);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Only call unlockBill on React SPA navigation — sendBeacon handles true page unloads
      if (!isUnloading && lockInfoRef.current) unlockBill();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unlockBill = async () => {
    const li = lockInfoRef.current;
    if (!li) return;
    try {
      await api.post('/api/unlockBill', { doc_no: li.doc_no, guid: li.guid });
    } catch (err) {
      console.error('unlockBill error:', err);
    }
    setLockInfo(null);
    lockInfoRef.current = null;
  };

  // เมื่อโหลดหน้าครั้งแรก ให้ดึงข้อมูล sales, customer, branch, default header มาเซ็ต state
  useEffect(() => {
    if (!currentUser?.user_code) return;

    (async () => {
      try {
        // 1) ดึง header, sales, customers, branch พร้อมกัน
        const [hdrRes, salesRes, custRes, branchRes] = await Promise.all([
          api.post("/api/getDefaultCartHeader",
                  { user: currentUser.user_code },
                  { withCredentials: true }),
          api.post("/api/getSalespersons", {}, { withCredentials: true }),
          api.post("/api/customerList",    {}, { withCredentials: true }),
          api.post("/api/getBranchList",   {}, { withCredentials: true }),
        ]);

        const header = hdrRes.data;
        setSalespersons(salesRes.data);
        setCustomers(custRes.data);
        setBranchList(branchRes.data);

        // 2) เซ็ตค่าหัวบิล
        setInvoiceDate(dayjs(header.doc_date));
        setCustomerId(header.cust_code);
        setCustomer(header.cust_name);
        setInputValue(header.cust_name);
        setArType(header.inquiry_type);
        setCreditDay(header.credit_day);
        setEmail(header.email || '');
        setPriceType(priceLevelMap[header.price_level]);
        setTaxType(header.set_tax_type);
        setShippingType(header.send_type);
        setDeliveryDate(dayjs(header.send_date));
        setDiscount(Number(header.discount).toFixed(2));
        savedDiscountRef.current = Number(header.discount).toFixed(2);
        setTotalSumAmount(header.total_sum_amount);
        setPromoDiscount(Number(header.promo_discount || 0));
        setLoyaltyDiscount(Number(header.loyalty_discount || 0));
        setLoyaltyPointsRedeemed(Number(header.loyalty_points_redeemed || 0));
        
        let branchName;
        // 3) เซ็ตพนักงานขาย
        // ฟอร์มใหม่ → ใช้ผู้ใช้ปัจจุบันเสมอ (แสดง warning ถ้าหาไม่เจอ)
        // แก้ไขเอกสาร + มี sale_code → ใช้ค่าที่บันทึกไว้
        // แก้ไขเอกสาร + ไม่มี sale_code → ไม่ auto-fill ใด ๆ
        const me = findCurrentSalesperson(salesRes.data, currentUser);
        setSaleCodeWasEmpty(isEditing ? !header.sale_code : !me);
        if (!isEditing) {
          if (me) {
            setSelectedSalesperson(me.code);
            setPickupBranch(me.branch_code);
            setBranch(me.branch_name);
            updateCartHeaderField("sale_code", me.code);
            updateCartHeaderField("pickup_branch", me.branch_code);
            updateCartHeaderField("branch_code", me.branch_code);
            branchName = me.branch_name;
          } else {
            Swal.fire({
              icon: 'warning',
              title: 'ไม่พบข้อมูลพนักงานขาย',
              text: 'ไม่พบรหัสพนักงานขายที่ตรงกับผู้ใช้งานปัจจุบัน กรุณาเลือกพนักงานขายด้วยตนเอง',
              confirmButtonText: 'ตกลง',
              confirmButtonColor: '#1976d2',
            });
          }
        } else if (header.sale_code) {
          setSelectedSalesperson(header.sale_code);
          setPickupBranch(header.pickup_branch);
          setBranch(header.branch_name);
          branchName = header.branch_name;
        }
        // isEditing && !header.sale_code → ไม่ทำอะไร ให้ dropdown ว่างเปล่า

        let newRemark = header.remark;
        if (!newRemark) {
          if (header.send_type === "รับเอง") {
            newRemark = branchName ? `รับของเอง + ${branchName}` : 'รับของเอง';
          } else if (header.send_type === "จัดส่ง") {
            // ดึงค่าขนส่งพร้อม remark จาก API คืนกลับมาเลย
            const { cost, invoiceRemark: shippingRemark } = await calculateShippingCost(
              header.cust_code,
              header.delivery_location_id,
              Number(header.total_sum_amount || 0) - Number(header.shipping_cost || 0),
              0,
              0
            );
            const addr = header.address;
            newRemark = shippingRemark || deliveryRemark(addr);
          }
        }
        setRemark(newRemark);
        setDraftNote(header.draft_note || '');
        setDraftPriority(header.draft_priority || 'waiting');

        // 4) ถ้าเป็น "จัดส่ง" ให้ตั้งค่า selectedLocation + shippingCost
        // (useEffect([shippingType, customerId]) จะ fetch deliveryLocations เอง)
        if (header.send_type === 'จัดส่ง') {
          setSelectedLocation(header.delivery_location_id);
          const cost = Number(header.shipping_cost) || 0;
          setShippingCost(cost);
          setAddShipping(cost > 0 ? "yes" : "no");
          setIsShippingCostManual(true);
        } else {
          // รับเอง → รีเซ็ต delivery-related state
          setDeliveryLocations([]);
          setSelectedLocation("");
          setShippingCost(0);
          setAddShipping("no");
          // ร่าง/QT เก่าอาจมีค่าขนส่งค้างใน header — addQT จะลบออกจากยอดอีกรอบถ้าไม่ล้าง
          if (Number(header.shipping_cost) > 0) updateCartHeaderField("shipping_cost", 0);
        }

      } catch (error) {
        console.error("Error initializing cart header:", error);
      }
    })();
  }, [currentUser, currentOrderNo]);

  // Sync priceType display เมื่อ CartModal เปลี่ยน priceLevel ใน CartContext
  useEffect(() => {
    const text = priceLevelMap[priceLevel];
    if (text && text !== priceType) {
      setPriceType(text);
    }
  }, [priceLevel]);

  // Sync customer fields เมื่อ CartModal เปลี่ยนลูกค้าใน CartContext
  useEffect(() => {
    if (!ctxCustomerId || ctxCustomerId === customerId || !currentUser?.user_code) return;
    (async () => {
      try {
        const res = await api.post("/api/getDefaultCartHeader", { user: currentUser.user_code }, { withCredentials: true });
        const header = res.data;
        if (!header) return;
        setCustomerId(header.cust_code);
        setCustomer(header.cust_name || "");
        setInputValue(header.cust_name || "");
        setArType(header.inquiry_type);
        setCreditDay(header.credit_day);
        setEmail(header.email || '');
        setPriceType(priceLevelMap[header.price_level]);
        setTaxType(header.set_tax_type);
        setShippingType(header.send_type);
      } catch (err) {
        console.error("Error syncing cart header from CartContext:", err);
      }
    })();
  }, [ctxCustomerId]);

  // เมื่อพนักงานขายไม่ได้อยู่ในแผนกขายเครดิต ให้รีเซ็ตประเภทบิลเป็นเงินสดและล้างวันเครดิต
  useEffect(() => {
    if (!salespersons.length || !selectedSalesperson) return;
    if (!isCreditGroup) {
      if (arType === 'เครดิต') {
        setArType('เงินสด');
        updateCartHeaderField('inquiry_type', 1);
      }
      if (creditDay !== 0) {
        setCreditDay(0);
        updateCartHeaderField('credit_day', 0);
      }
    }
  // updateCartHeaderField is recreated every render but only writes static literal
  // values here, so omitting it from deps is intentional and safe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreditGroup, arType, creditDay, salespersons.length, selectedSalesperson]);

  const handleInvoiceDateChange = (newValue) => {
    setInvoiceDate(newValue);
    setDeliveryDate(newValue);
    if (newValue) {
      updateCartHeaderField("doc_date", newValue.format("YYYY-MM-DD"));
      updateCartHeaderField("send_date", newValue.format("YYYY-MM-DD"));
    }
  };
  const handleDeliveryDateChange = (newValue) => {
    setDeliveryDate(newValue);
    if (newValue) updateCartHeaderField("send_date", newValue.format("YYYY-MM-DD"));
  };

  const handleArTypeChange = (eOrValue) => {
    let inputVal = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    if (inputVal == null) {
      setArType(null);
      updateCartHeaderField("inquiry_type", null);
    } else {
      const value = inputVal === "เครดิต" ? 0 : 1;
      setArType(inputVal);
      updateCartHeaderField("inquiry_type", value);
      if (inputVal === 'เงินสด' && creditDay !== 0) {
        setCreditDay(0);
        updateCartHeaderField('credit_day', 0);
      }
    }
  };

  const handlePriceTypeChange = (eOrValue) => {
    let selectedPriceText = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    if (selectedPriceText == null) {
      setPriceType(null);
      setPriceLevel(0);
      updateCartPrices(null);
      updateCartHeaderField("price_level", null);
    } else {
      const selectedPriceLevel =
        Object.keys(priceLevelMap).find(key => priceLevelMap[key] === selectedPriceText) || 0;
      setPriceType(selectedPriceText);
      setPriceLevel(Number(selectedPriceLevel));
      updateCartPrices(selectedPriceLevel);
      updateCartHeaderField("price_level", selectedPriceLevel);
    }
  };

  const handleTaxTypeChange = (eOrValue) => {
    let inputVal = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    setTaxType(inputVal);
    const newValue = inputVal === 'vat' ? '' : inputVal;
    updateCartHeaderField("set_tax_type", newValue);
  };

  const handleShippingTypeChange = async (eOrValue) => {
    // ดึงค่าที่เลือก
    let inputVal = eOrValue && eOrValue.target
      ? eOrValue.target.value
      : eOrValue;

    if (inputVal == null) {
      setShippingType(null);
      await updateCartHeaderField("send_type", null);
      return;
    }

    const isDelivery = inputVal === "จัดส่ง";
    setShippingType(inputVal);
    await updateCartHeaderField("send_type", isDelivery ? 1 : 0);
    if (!isDelivery) {
      // รับเอง → ไม่มีค่าขนส่ง (ทั้งจอและ header) แต่คงที่อยู่จัดส่ง/ช่องติ้กไว้ เปลี่ยนกลับเป็นจัดส่งจะคิดค่าส่งใหม่
      setShippingCost(0);
      setIsShippingCostManual(false);
      await updateCartHeaderField("shipping_cost", 0);
    }

    let newRemark = "";
    if (inputVal === "รับเอง") {
      // รับเอง → รับของเอง + ชื่อสาขา
      newRemark = `รับของเอง + ${branch}`;
    } else if (inputVal === "จัดส่ง") {
      // จัดส่ง → ถ้ามี selectedLocation ใช้ address ถ้ายังไม่เลือกเว้นว่าง
      const loc = deliveryLocations.find(l => l.roworder === selectedLocation);
      newRemark = loc ? deliveryRemark(loc.address) : "";
    }
    setRemark(newRemark);
    updateCartHeaderField("remark", newRemark);

  };

  const handleCreditDayChange = (eOrValue) => {
    let inputVal = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    const value = parseInt(inputVal, 10);
    const creditDayValue = !isNaN(value) ? value : 0;
    setCreditDay(creditDayValue);
    updateCartHeaderField("credit_day", creditDayValue);
  };

  const handleSalePersonChange = (eOrValue) => {
    const inputVal = eOrValue && eOrValue.target ? eOrValue.target.value : eOrValue;
    setSelectedSalesperson(inputVal);
    updateCartHeaderField("sale_code", inputVal);

    const selectedSale = salespersons.find((sale) => sale.code === inputVal);
    if (selectedSale) {
      setPickupBranch(selectedSale.branch_code);
      setBranch(selectedSale.branch_name);
      updateCartHeaderField("branch_code", selectedSale.branch_code);
      updateCartHeaderField("pickup_branch", selectedSale.branch_code);
    }
  };

  const handlePickupBranchChange = (e) => {
    const selectedCode = e.target.value;
    setPickupBranch(selectedCode);
    updateCartHeaderField("pickup_branch", selectedCode);
    const branchInfo = branchList.find(branch => branch.code === selectedCode);
    if (branchInfo) {
      setBranch(branchInfo.name_1);
      if (shippingType === "รับเอง") {
        const newRemark = `รับของเอง + ${branchInfo.name_1}`;
        setRemark(newRemark);
        updateCartHeaderField("remark", newRemark);
      }
    }
  };

  const handleSelectedLocationChange = async (e) => {
    const locId = e.target.value;
    if (!locId) {
      setSelectedLocation("");
      await updateCartHeaderField("delivery_location_id", null);
      setAddShipping("no");
      // ถ้าไม่ได้เลือกเพิ่มค่าขนส่ง ก็ไม่แตะ shippingCost
      if (addShipping === "yes") {
        setShippingCost(0);
        await updateCartHeaderField("shipping_cost", 0);
      }
      // รีเซ็ต remark เสมอเมื่อล้างที่อยู่
      setRemark("");
      await updateCartHeaderField("remark", "");
      return;
    }

    setSelectedLocation(locId);
    await updateCartHeaderField("delivery_location_id", locId);

    // หา address
    const locObj = deliveryLocations.find(l => l.roworder === locId);
    const addr = locObj?.address || "";

    let invoiceRemark = "";
    let shippingInfo = "";

    // เรียก API เมื่อ user เลือกเพิ่มค่าขนส่งเท่านั้น
    if (addShipping === "yes") {
      const { cost, invoiceRemark: ir, shippingInfo: si } =
        await calculateShippingCost(customerId, locId);
      setShippingCost(cost);
      await updateCartHeaderField("shipping_cost", cost);
      shippingInfo = si;
      invoiceRemark = ir;
    }

    // คำนวณ remark ใหม่ (รับเองหรือจัดส่ง)
    let newRemark;
    if (shippingType === "รับเอง") {
      newRemark = `รับของเอง + ${branch}`;
    } else { // จัดส่ง
      newRemark = invoiceRemark || deliveryRemark(addr);
    }

    setRemark(newRemark);
    await updateCartHeaderField("remark", newRemark);
  };


  const handleShippingCostChange = (input) => {
    let cost = typeof input === 'number'
      ? input
      : Number(input.target.value);
    setShippingCost(cost);
    setIsShippingCostManual(true);
    updateCartHeaderField("shipping_cost", cost);
  };

  const handleRemarkChange = (e) => {
    const value = e.target.value;
    setRemark(value);
    updateCartHeaderField("remark", value);
  };

  // ดึงสถานะสต็อคจาก API แล้ว merge ผลเข้า updatedRows
  const fetchStockStatus = async (cartItems) => {
    try {
      const { data } = await api.post(
        '/api/check-stock',
        {
          itemCodes: cartItems.map(i => i.item_code),
          standValues: cartItems.map(i => i.stand_value),
          divideValues: cartItems.map(i => i.divide_value),
        },
        { withCredentials: true }
      );
      const { stockData } = data;
      if (!Array.isArray(stockData)) throw new Error('Invalid stockData');

      const stockLookup = {};
      cartItems.forEach(item => {
        const key = item.roworder;
        const match = s =>
          s.item_code === item.item_code &&
          Number(s.stand_value) === Number(item.stand_value) &&
          Number(s.divide_value) === Number(item.divide_value);

        const totalRow = stockData.find(s => match(s) && s.warehouse === 'รวม');
        const totalStockQty = totalRow ? parseFloat(totalRow.balance_qty) || 0 : 0;
        const totalAccruedIn = totalRow ? parseFloat(totalRow.accrued_in_qty) || 0 : 0;
        const totalAccruedOut = totalRow ? parseFloat(totalRow.accrued_out_qty) || 0 : 0;
        const availableQty = totalStockQty - totalAccruedOut;
        const isInStock = availableQty >= Number(item.qty);

        // Always build per-branch breakdown
        const seen = new Set();
        const branches = [];
        stockData.forEach(s => {
          if (match(s) && s.branch_name && !seen.has(s.branch_name)) {
            seen.add(s.branch_name);
            branches.push({ name: s.branch_name, qty: parseFloat(s.balance_qty) || 0 });
          }
        });

        // Warning: total stock is enough but the selected local warehouse is short
        let isLocalShort = false;
        if (isInStock) {
          if (shippingType === 'รับเอง' && pickupBranch) {
            const branchQty = stockData
              .filter(s => match(s) && s.branch_code === pickupBranch)
              .reduce((sum, s) => sum + (parseFloat(s.balance_qty) || 0), 0);
            isLocalShort = branchQty < Number(item.qty);
          } else {
            const main = stockData.find(s => match(s) && s.main_branch === 1);
            const mainQty = main ? parseFloat(main.balance_qty) || 0 : 0;
            isLocalShort = mainQty < Number(item.qty);
          }
        }

        stockLookup[key] = { isInStock, isLocalShort, totalStockQty, totalAccruedIn, totalAccruedOut, branches };
      });

      setUpdatedRows(prevRows =>
        prevRows.map(row => {
          const upd = stockLookup[row.roworder];
          return upd
            ? { ...row, ...upd }
            : row;
        })
      );
    } catch (err) {
      console.error('Error fetching stock status:', err);
    }
  };

  // Debounce stock checks: one edit can change `cart` several times (local
  // fetchCart + CART_UPDATED broadcast), and back-to-back edits would otherwise
  // hit the backend rate limit. Only the latest rows are sent.
  const fetchStockStatusRef = useRef(fetchStockStatus);
  fetchStockStatusRef.current = fetchStockStatus;
  const stockFetchTimer = useRef(null);
  const scheduleStockFetch = useCallback((rows) => {
    clearTimeout(stockFetchTimer.current);
    stockFetchTimer.current = setTimeout(() => fetchStockStatusRef.current(rows), 400);
  }, []);
  useEffect(() => () => clearTimeout(stockFetchTimer.current), []);

  // ฟังก์ชันประมวลผลเมื่อแก้ไขแถว (กด Enter หรือคลิก check)
  const processRowUpdate = useCallback(
    async (newRow, oldRow) => {
      // จำนวนต้องมากกว่า 0 (ทศนิยมได้) — ตรวจเฉพาะเมื่อแก้จำนวน, server ตรวจซ้ำ (H8)
      const invalidQty = newRow.qty !== oldRow.qty && !(Number(newRow.qty) > 0);
      // ราคาต้องไม่ติดลบ — ตรวจเฉพาะเมื่อแก้ราคา, server ตรวจซ้ำ (H7-4)
      const invalidPrice = newRow.price !== oldRow.price && !(Number(newRow.price) >= 0);
      if (invalidQty || invalidPrice) {
        Swal.fire({
          icon: 'error',
          title: invalidQty ? 'จำนวนต้องมากกว่า 0' : 'ราคาต้องไม่ติดลบ',
          confirmButtonText: 'ตกลง',
          confirmButtonColor: '#1976d2',
        });
        return oldRow;
      }

      const payload = { roworder: newRow.roworder };
      let shouldRefetchStock = false;

      if (newRow.item_name !== oldRow.item_name) payload.item_name = newRow.item_name;
      if (newRow.unit_code !== oldRow.unit_code) {
        payload.unit_code = newRow.unit_code;
        shouldRefetchStock = true;

        // ดึง metadata ของหน่วยใหม่
        const units = unitOptions[newRow.item_code]?.units || [];
        const sel = units.find((u) => u.code === newRow.unit_code);
        if (sel) {
          payload.stand_value = sel.stand_value;
          payload.divide_value = sel.divide_value;
          newRow.stand_value = sel.stand_value;
          newRow.divide_value = sel.divide_value;

          const oldRaw = oldRow.stand_value / oldRow.divide_value;
          const baseWeight = oldRow.weight / oldRaw;
          const newRaw = sel.stand_value / sel.divide_value;
          newRow.weight = baseWeight * newRaw;

          const priceFactor = newRaw / oldRaw;
          // หน่วยใหม่มีสูตรราคาของตัวเอง → ใช้ราคานั้น (ยกเว้นแถวที่แก้ราคาเอง ให้แปลงตามสัดส่วน)
          const ownPrice = (manualPriceEdits.has(oldRow.roworder) || oldRow.price_manual) ? null : ownUnitPrice(sel, priceLevel);
          const convertedPrice = ownPrice ?? oldRow.price * priceFactor;
          newRow.price = Number(convertedPrice.toFixed(2));
          payload.unitPrice = newRow.price;

          // Convert qty to preserve base quantity (same real-world amount, different display unit)
          const convertedQty = Number(oldRow.qty) / priceFactor;
          newRow.qty = parseFloat(convertedQty.toFixed(4));
          payload.qty = newRow.qty;
        }
      }
      if (newRow.qty !== oldRow.qty) {
        payload.qty = newRow.qty;
        shouldRefetchStock = true;
      }
      if (newRow.price !== oldRow.price) {
        payload.unitPrice = newRow.price;
        // ราคาเปลี่ยนเพราะเปลี่ยนหน่วย ไม่นับเป็นการแก้ราคาเอง
        if (newRow.unit_code === oldRow.unit_code) {
          setManualPriceEdits(prev => new Set([...prev, newRow.roworder]));
        }
      }

      if (Object.keys(payload).length > 1) {
        await updateDetailsItemInCart(payload);

        const newRows = updatedRows.map((row) =>
          row.roworder === newRow.roworder
            ? {
              ...row,
              ...newRow,
              sum_amount: newRow.qty * newRow.price,
            }
            : row
        );

        setUpdatedRows(newRows);

        if (shouldRefetchStock) {
          scheduleStockFetch(newRows);
        }
      }
      return newRow;
    },
    [updateDetailsItemInCart, updatedRows, unitOptions, scheduleStockFetch, manualPriceEdits, priceLevel]
  );

  const handleProcessRowUpdateError = useCallback((error) => {
    setSnackbar({ children: error.message, severity: "error" });
  }, []);

  // Helper ฟอร์แมตราคา
  const handleUnitChange = (params, newUnitCode) => {
    params.api.setEditCellValue({ id: params.id, field: 'unit_code', value: newUnitCode });
  };

  // ฟังก์ชันดึงหน่วยของสินค้า (unit options)
  useEffect(() => {
    const fetchUnitOptions = async () => {
      try {
        const response = await api.get("/api/unitOptions");
        if (Array.isArray(response.data)) {
          const unitData = response.data.reduce((acc, { ic_code, units, unit_base }) => {
            acc[ic_code] = { units, unit_base };
            return acc;
          }, {});
          setUnitOptions(unitData);
        } else {
          console.error("Invalid API response format");
        }
      } catch (error) {
        console.error("Error fetching unit options:", error);
      }
    };
    fetchUnitOptions();
  }, []);

  // คำนวณน้ำหนักรวมจาก cart แล้วเซ็ต state
  useEffect(() => {
    const totalWeight = cart.reduce(
      (acc, item) => acc + (Number(item.weight) || 0) * (Number(item.qty) || 0),
      0
    );
    setTotalWeight(totalWeight);
  }, [cart]);

  // คำนวณความยาวสูงสุด (length) จาก cart
  useEffect(() => {
    if (cart && cart.length > 0) {
      const maxLen = Math.max(...cart.map(item => Number(item.length) || 0));
      setMaxLength(maxLen);
    } else {
      setMaxLength(0);
    }
  }, [cart]);

  // คำนวณยอดรวมสินค้า แล้วอัปเดต totalSumAmount
  useEffect(() => {
    if (!cart || cart.length === 0) return;
    const timer = setTimeout(() => {
      const { totalAfterDiscount: newTotalSum } = computeCheckoutTotals({
        cart, shippingType, shippingCost, discount,
        promoDiscount: effectivePromoDiscount, loyaltyDiscount,
      });
      setTotalSumAmount(newTotalSum);
      updateCartHeaderField("total_sum_amount", newTotalSum);
    }, 400);
    return () => clearTimeout(timer);
  }, [cart, shippingType, shippingCost, discount, effectivePromoDiscount, loyaltyDiscount]);

  // ถ้าเป็นกรณีจัดส่ง และยังไม่ได้ override ค่า shippingCost, แล้วมีที่อยู่แล้ว → debounce เพื่อคำนวณ shipping ทุกครั้งที่ข้อมูลเปลี่ยน
  useEffect(() => {
    if (
      shippingType !== "จัดส่ง" ||
      addShipping !== "yes" ||
      isShippingCostManual ||
      !selectedLocation
    ) return;

    const debounceTimer = setTimeout(() => {
      calculateShippingCost(
        customerId,
        selectedLocation,
        getShippingBaseAmount(),
        totalWeight,
        maxLength
      ).then(({ cost }) => {
        setShippingCost(cost);
        updateCartHeaderField("shipping_cost", cost);
      });
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [
    cart,
    totalWeight,
    maxLength,
    selectedLocation,
    customerId,
    shippingType,
    addShipping,
    isShippingCostManual,
    discount,
  ]);

  // Auto-evaluate promotions (debounced, P5 — read-only)
  useEffect(() => {
    if (!cartGuid) return;
    const timer = setTimeout(() => {
      api.post('/api/promotions/evaluate', { cart_guid: cartGuid }, { withCredentials: true })
        .then(({ data }) => {
          hasEvaluatedRef.current = true;
          setAppliedPromos(data.applied || []);
          setPromoDiscount(data.total_promo_discount || 0);
        })
        .catch(() => {});
    }, 200);
    return () => clearTimeout(timer);
  }, [cart, cartGuid]);

  // Loyalty summary fetch when customer changes
  useEffect(() => {
    if (!customerId) return;
    api.post('/api/loyalty/summary', { cust_code: customerId }, { withCredentials: true })
      .then(({ data }) => setLoyaltyPoints(data.available_points || 0))
      .catch(() => {});
  }, [customerId]);

  // Loyalty points-to-earn preview (debounced)
  useEffect(() => {
    if (!cartGuid) return;
    const timer = setTimeout(() => {
      api.post('/api/loyalty/preview-earn', { cart_guid: cartGuid }, { withCredentials: true })
        .then(({ data }) => setPointsToEarn(data.points_to_earn || 0))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [cart, cartGuid]);

  // Load applied promos from DB on mount (code-promos ที่ถูก persist ไว้)
  // ไม่ทับผล evaluate — evaluate มีสิทธิ์สูงกว่าเสมอ
  useEffect(() => {
    if (!cartGuid) return;
    hasEvaluatedRef.current = false; // reset เมื่อ cartGuid เปลี่ยน
    api.post('/api/promotions/listApplied', { cart_guid: cartGuid }, { withCredentials: true })
      .then(({ data }) => {
        if (hasEvaluatedRef.current) return; // evaluate วิ่งทันและตั้งค่าไปแล้ว
        setAppliedPromos(data || []);
        const total = (data || []).filter(p => !p.is_free_gift).reduce((s, p) => s + Number(p.discount_amount), 0);
        setPromoDiscount(total);
      })
      .catch(() => {});
  }, [cartGuid]);

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim() || !cartGuid) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const { data } = await api.post('/api/promotions/apply',
        { cart_guid: cartGuid, promo_code: promoCode.trim() },
        { withCredentials: true }
      );
      setAppliedPromos(data.applied || []);
      setPromoDiscount(data.total_promo_discount || 0);
      setPromoCode('');
    } catch (err) {
      setPromoError(err.response?.data?.error || 'ไม่พบโปรโมชันที่ตรงกัน');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = async (appliedPromoId) => {
    try {
      await api.post('/api/promotions/remove',
        { applied_promo_id: appliedPromoId, cart_guid: cartGuid },
        { withCredentials: true }
      );
      const { data } = await api.post('/api/promotions/listApplied',
        { cart_guid: cartGuid }, { withCredentials: true }
      );
      setAppliedPromos(data || []);
      const newTotal = (data || []).filter(p => !p.is_free_gift).reduce((s, p) => s + Number(p.discount_amount), 0);
      setPromoDiscount(newTotal);
    } catch (err) {
      console.error('remove promo error:', err);
    }
  };

  const handleRedeemPoints = async () => {
    const pts = Number(redeemInput);
    if (!pts || pts <= 0 || !cartGuid) return;
    setLoyaltyLoading(true);
    try {
      const { data } = await api.post('/api/loyalty/redeem',
        { cart_guid: cartGuid, points_to_redeem: pts },
        { withCredentials: true }
      );
      setLoyaltyDiscount(data.loyalty_discount || 0);
      setLoyaltyPointsRedeemed(data.points_used || 0);
      setRedeemInput('');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || 'ไม่สามารถแลกแต้มได้', confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' });
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const handleRemoveRedemption = async () => {
    if (!cartGuid) return;
    try {
      await api.post('/api/loyalty/remove-redemption', { cart_guid: cartGuid }, { withCredentials: true });
      setLoyaltyDiscount(0);
      setLoyaltyPointsRedeemed(0);
    } catch (err) {
      console.error('remove redemption error:', err);
    }
  };

  // // ถ้าเป็นจัดส่ง และมี customerId → ดึงที่อยู่จัดส่งจาก backend
  useEffect(() => {
    // ถ้าเปลี่ยนมาเป็น "จัดส่ง" ให้ fetch locations
    if (shippingType === "จัดส่ง" && customerId) {
      api.post(`/api/getCustomerDeliveryLocations?customer_id=${encodeURIComponent(customerId)}`, null, { withCredentials: true })
        .then(res => {
          setDeliveryLocations(res.data.locations || []);
        })
        .catch(console.error);
    } else {
      // ถ้าเปลี่ยนเป็น "รับเอง" → ล้าง state
      setDeliveryLocations([]);
    }
  }, [shippingType, customerId]);

  

  useEffect(() => {
    // คำนวณ list ของแถวใหม่จาก cart
    const newRows = cart.map((item, idx) => {
      const existing = updatedRows.find(r => r.roworder === item.roworder) || {};
      return {
        ...item,
        price: Number(item.price),
        stand_value: Number(item.stand_value),
        divide_value: Number(item.divide_value),
        rowIndex: idx + 1,
        sum_amount: Number(item.qty) * Number(item.price),
        isInStock: existing.isInStock != null ? existing.isInStock : null,
        isLocalShort: existing.isLocalShort ?? false,
        totalStockQty: existing.totalStockQty ?? 0,
        totalAccruedIn: existing.totalAccruedIn ?? 0,
        totalAccruedOut: existing.totalAccruedOut ?? 0,
        branches: existing.branches ?? [],
      };
    });

    // ตั้งค่า updatedRows และรีเช็คสต็อค (เฉพาะตอนเปลี่ยน cart)
    setUpdatedRows(newRows);
    if (newRows.length > 0) {
      scheduleStockFetch(newRows);
    }
  }, [cart]);

  // เมื่อ pickupBranch หรือ shippingType เปลี่ยน → refetch สต็อคใหม่
  useEffect(() => {
    if (updatedRows.length > 0) {
      scheduleStockFetch(updatedRows);
    }
  }, [pickupBranch, shippingType]);

  // เมื่อเข้า path '/checkout' ครั้งแรก ให้ fetch สต็อค
  const location = useLocation();
  const hasFetchedOnCheckout = useRef(false);
  useEffect(() => {
    if (
      location.pathname === '/checkout' &&
      updatedRows.length > 0 &&
      !hasFetchedOnCheckout.current
    ) {
      scheduleStockFetch(updatedRows);
      hasFetchedOnCheckout.current = true;
    }
  }, [location.pathname, updatedRows]);

  // Cache สำหรับดึงราคาต้นฉบับ (originalPrices) ไม่ให้ fetch ซ้ำ
  const fetchedPriceCache = useRef(new Set());

  useEffect(() => {
    fetchedPriceCache.current.clear();
    setOriginalPrices({});
  }, [customerId]);

  useEffect(() => {
    async function fetchOriginalPrices() {
      const newKeys = Array.from(
        new Set(
          cart
            .map((item) => `${item.item_code}_${item.unit_code}`)
            .filter((key) => !fetchedPriceCache.current.has(key))
        )
      );
      if (newKeys.length === 0) return;

      try {
        const responses = await Promise.all(
          newKeys.map((key) => {
            const [code, unit_code] = key.split("_");
            return api.get("/api/getOriginalPrice", {
              params: { cust_code: customerId, item_code: code, unit_code: unit_code },
            });
          })
        );

        const newPrices = {};
        responses.forEach((res, idx) => {
          const key = newKeys[idx];
          newPrices[key] = res.data?.[0]?.price || null;
          fetchedPriceCache.current.add(key);
        });


        setOriginalPrices((prev) => ({ ...prev, ...newPrices }));
      } catch (err) {
        console.error("Error fetching original prices:", err);
      }
    }

    if (cart.length > 0 && customerId) {
      fetchOriginalPrices();
    }
  }, [cart, customerId]);

  // อัปเดตราคาในตะกร้าตาม level
  const updateCartPrices = async (priceLevel) => {
    try {
      await api.post("/api/updateCartPriceByLevel", { price_level: priceLevel });
      fetchedPriceCache.current.clear();
      setOriginalPrices({});
      fetchCart();
    } catch (error) {
      console.error("Error updating cart prices:", error);
    }
  };

  // ฟังก์ชันค้นหาลูกค้า (autocomplete) — debounced 300 ms, aborts stale requests
  const handleSearch = (event, value) => {
    if (!value || value.length < 2) {
      setCustomers([]);
      return;
    }

    // Cancel any in-flight request
    if (searchAbortRef.current) searchAbortRef.current.abort();
    // Clear pending debounce
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    searchDebounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      setLoading(true);
      try {
        const response = await api.post(
          `/api/customerList?search=${encodeURIComponent(value)}`,
          null,
          { signal: controller.signal }
        );
        setCustomers(response.data);
      } catch (error) {
        if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error("Error fetching customer list:", error);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleCustomerChange = async (event, value) => {
    if (value) {
      // 1. Reset delivery-related state FIRST — prevents the delivery-locations
      //    useEffect from racing against these resets when shippingType/customerId change.
      setSelectedLocation("");
      setDeliveryLocations([]);
      setShippingCost(0);
      setIsShippingCostManual(false);

      // 2. Set customer identity fields
      setInputValue(value.name_1 || "");
      setCustomer(value.name_1);
      setCustomerId(value.code);
      setCreditDay(value.credit_day);
      setEmail(value.email);

      // 3. Apply customer defaults — sync state setters, backend writes fire in background
      setSaleCodeWasEmpty(!value.sale_code);
      handleSalePersonChange(value.sale_code);
      handleArTypeChange(value.sale_type);
      handleTaxTypeChange(value.set_tax_type);
      handleCreditDayChange(value.credit_day);
      setAddShipping(value.sale_type === "เครดิต" ? "no" : "yes");

      // 4. Await the backend writes that need a guaranteed order
      await updateCartHeaderField("cust_code", value.code);
      // ระดับราคาต้องตั้งหลังบันทึก cust_code — backend ตรวจว่าตรงกับระดับราคาของลูกค้าในตะกร้า
      handlePriceTypeChange(priceLevelMap[value.price_level]);
      await updateCartHeaderField("delivery_location_id", null);
      await updateCartHeaderField("shipping_cost", 0);

      // 5. Shipping type last — it awaits a backend write and sets remark.
      //    Changing shippingType and/or pickupBranch (via handleSalePersonChange above)
      //    will trigger the [pickupBranch, shippingType] effect, which re-checks stock.
      await handleShippingTypeChange(value.sale_type === "เครดิต" ? "จัดส่ง" : "รับเอง");
    } else {
      setCustomer("");
      setCustomerId("");
      setArType("");
      setPriceType("สด");
      setCreditDay(0);
      setSelectedLocation("");
      setDeliveryLocations([]);
      setShippingCost(0);
    }
  };

  // ฟังก์ชันจัดการเมื่อ user ปรับแต่ง discount แล้วออกจาก input
  const handleDiscountChange = (e) => {
    const value = e.target.value;
    setDiscount(value);
  };

  const handleDiscountBlur = async (e) => {
    if (!canEditDiscount) return;
    const raw = parseFloat(e.target.value);
    const totalBeforeDiscount = (totalSumAmount || 0) + Number(discount || 0);
    // server รับส่วนลดได้ไม่เกินยอดสินค้า (ไม่รวมค่าขนส่ง)
    const cap = Math.max(0, Math.min(totalBeforeDiscount, sumProducts(cart)));
    const clamped = Math.min(Math.max(0, isNaN(raw) ? 0 : raw), cap);
    const formatted = clamped.toFixed(2);
    setDiscount(formatted);
    if (formatted === savedDiscountRef.current) return;
    try {
      await api.post("/api/updateCartHeaderField", { fieldName: "discount", fieldValue: clamped });
      savedDiscountRef.current = formatted;
    } catch (err) {
      // ไม่ให้จอแสดงส่วนลดที่ server ไม่ได้บันทึก (ยอดบนจอกับ QT จะไม่ตรงกัน)
      setDiscount(savedDiscountRef.current);
      Swal.fire({
        icon: 'error',
        title: 'บันทึกส่วนลดไม่สำเร็จ',
        text: err.response?.data?.error || 'กรุณาลองใหม่อีกครั้ง',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  // Cell blur: save changed value then clear editingCell
  const handleCellBlur = useCallback(async (row, field, value) => {
    setEditingCell(null);
    const oldRow = updatedRows.find(r => r.roworder === row.roworder);
    if (!oldRow) return;
    let parsedValue = value;
    if (field === 'qty' || field === 'price') {
      parsedValue = Number(value);
      if (isNaN(parsedValue)) return;
      if (field === 'qty' && parsedValue <= 0) return;
      if (field === 'price' && parsedValue < 0) return;
    }
    if (String(parsedValue) === String(oldRow[field])) return;
    const newRow = { ...oldRow, [field]: parsedValue };
    await processRowUpdate(newRow, oldRow);
  }, [updatedRows, processRowUpdate]);

  // Drag-and-drop reorder — Pointer Events so it works with mouse, pen and touch
  // (HTML5 drag-and-drop does not fire from touch on tablets/phones)
  const moveRow = (fromId, targetRoworder) => {
    if (fromId == null || targetRoworder == null || fromId === targetRoworder) return;

    setUpdatedRows(prevRows => {
      const cartItems = prevRows.filter(r => r.roworder !== -1);
      const other = prevRows.filter(r => r.roworder === -1);
      const fromIdx = cartItems.findIndex(r => r.roworder === fromId);
      const toIdx = cartItems.findIndex(r => r.roworder === targetRoworder);
      if (fromIdx === -1 || toIdx === -1) return prevRows;
      const newCart = [...cartItems];
      const [moved] = newCart.splice(fromIdx, 1);
      newCart.splice(toIdx, 0, moved);
      const lines = newCart.map((r, i) => ({ roworder: r.roworder, line_number: i + 1 }));
      api.post('/api/reorderCartLines', { lines }).then(() => fetchCart()).catch(console.error);
      return [...newCart.map((r, i) => ({ ...r, rowIndex: i + 1 })), ...other];
    });
  };

  // Close the stock tooltip when tapping/clicking anywhere outside a stock dot
  useEffect(() => {
    if (stockTipId == null) return;
    const handlePointerDown = (e) => {
      if (!e.target.closest?.('[data-stock-tip]')) setStockTipId(null);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [stockTipId]);

  const resetDrag = () => {
    dragItemRef.current = null;
    dragOverRef.current = null;
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleHandlePointerDown = (e, roworder) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragItemRef.current = roworder;
    dragOverRef.current = roworder;
    setDraggingId(roworder);
    setDragOverId(roworder);
  };

  const handleHandlePointerMove = (e) => {
    if (dragItemRef.current == null) return;
    e.preventDefault();

    // Auto-scroll the list when the pointer nears its top/bottom edge
    const box = scrollBoxRef.current;
    if (box) {
      const rect = box.getBoundingClientRect();
      const edge = 40;
      if (e.clientY < rect.top + edge) box.scrollTop -= 12;
      else if (e.clientY > rect.bottom - edge) box.scrollTop += 12;
    }

    const rowEl = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-roworder]');
    if (!rowEl) return;
    const target = finalRows.find(r => String(r.roworder) === rowEl.dataset.roworder)?.roworder;
    if (target == null || target === -1 || target === dragOverRef.current) return;
    dragOverRef.current = target;
    setDragOverId(target);
  };

  const handleHandlePointerUp = (e) => {
    if (dragItemRef.current == null) return;
    e.stopPropagation();
    const fromId = dragItemRef.current;
    const toId = dragOverRef.current;
    resetDrag();
    moveRow(fromId, toId);
  };

  // ล้างตะกร้า (clearCart) พร้อมยืนยันผ่าน SweetAlert
  const handleClearDetails = () => {
    Swal.fire({
      title: "ยืนยันการล้างข้อมูล",
      text: "คุณแน่ใจหรือไม่ว่าต้องการล้างข้อมูลทั้งหมดในตะกร้า?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#757575",
      confirmButtonText: "ล้างข้อมูล",
      cancelButtonText: "ยกเลิก",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await unlockBill();
        await deleteCart();
        Swal.fire({
          title: "เรียบร้อย",
          text: "ข้อมูลในตะกร้าถูกล้างเรียบร้อยแล้ว",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          navigate(-1);
        });
      }
    });
  };

  const handleSaveDraft = async () => {
    const PRIORITIES = [
      { value: 'urgent',  emoji: '🔴', label: 'ด่วนมาก' },
      { value: 'high',    emoji: '🟠', label: 'ด่วน' },
      { value: 'today',   emoji: '🟡', label: 'วันนี้' },
      { value: 'waiting', emoji: '🔵', label: 'รอ' },
    ];

    const result = await Swal.fire({
      title: 'บันทึก Draft',
      html: `
        <div style="text-align:left;width:100%">
          <div style="margin-bottom:16px">
            <div style="font-size:13px;color:#555;margin-bottom:8px;font-weight:500">Priority</div>
            <div style="display:flex;gap:8px;flex-wrap:wrap" id="swal-priority-group">
              ${PRIORITIES.map(p => `
                <button type="button" data-value="${p.value}"
                  style="cursor:pointer;padding:6px 14px;border:2px solid #e0e0e0;border-radius:20px;font-size:13px;background:transparent;transition:all .15s">
                  ${p.emoji} ${p.label}
                </button>
              `).join('')}
            </div>
          </div>
          <div>
            <div style="font-size:13px;color:#555;margin-bottom:6px;font-weight:500">หมายเหตุ Draft</div>
            <textarea id="swal-draft-note"
              style="width:100%;padding:8px 10px;border:1px solid #ccc;border-radius:4px;font-size:14px;resize:vertical;min-height:80px;box-sizing:border-box;font-family:inherit;outline:none"
              placeholder="กรอกหมายเหตุเพิ่มเติม..."></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#757575',
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      focusConfirm: false,
      didOpen: () => {
        const textarea = document.getElementById('swal-draft-note');
        if (textarea) textarea.value = draftNote || '';

        const btns = document.querySelectorAll('#swal-priority-group button');
        const selectPriority = (val) => {
          btns.forEach(btn => {
            const sel = btn.dataset.value === val;
            btn.dataset.selected = sel ? 'true' : 'false';
            btn.style.borderColor = sel ? '#1976d2' : '#e0e0e0';
            btn.style.background  = sel ? '#e3f2fd' : 'transparent';
            btn.style.fontWeight  = sel ? '600' : 'normal';
          });
        };
        selectPriority(draftPriority || 'waiting');
        btns.forEach(btn => btn.addEventListener('click', () => selectPriority(btn.dataset.value)));
      },
      preConfirm: () => ({
        priority: document.querySelector('#swal-priority-group button[data-selected="true"]')?.dataset?.value || 'waiting',
        note: document.getElementById('swal-draft-note')?.value?.trim() || '',
      }),
    });

    if (!result.isConfirmed) return;
    const { priority, note } = result.value;

    try {
      const saveRes = await api.post("/api/saveCartAsDraft", { note, priority, remark }, { withCredentials: true });
      const draftRef = saveRes.data?.draftRef || '';
      await unlockBill();
      resetCartState();
      await Swal.fire({
        icon: "success",
        title: "บันทึก Draft สำเร็จ!",
        html: `
          <div style="text-align:center;padding:6px 0">
            <div style="font-size:13px;color:#757575;margin-bottom:6px">เลขที่บิล</div>
            <div style="display:flex;align-items:center;justify-content:center;gap:8px">
              <span style="font-size:20px;font-weight:700;color:#1976d2;letter-spacing:1px">${draftRef}</span>
              <button id="swal-copy-btn" style="background:#f5f5f5;border:1px solid #ddd;border-radius:6px;padding:4px 10px;font-size:12px;cursor:pointer;font-family:inherit">คัดลอก</button>
            </div>
            <div id="swal-copy-msg" style="color:#2e7d32;font-size:12px;min-height:18px;margin-top:6px"></div>
          </div>
        `,
        showConfirmButton: true,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#1976d2',
        didOpen: () => {
          document.getElementById('swal-copy-btn').addEventListener('click', () => {
            const doCopy = () => {
              const el = document.createElement('textarea');
              el.value = draftRef;
              el.style.cssText = 'position:fixed;opacity:0';
              document.body.appendChild(el);
              el.focus();
              el.select();
              document.execCommand('copy');
              document.body.removeChild(el);
            };
            const finish = () => {
              const msg = document.getElementById('swal-copy-msg');
              if (msg) msg.textContent = 'คัดลอกแล้ว!';
              const btn = document.getElementById('swal-copy-btn');
              if (btn) { btn.textContent = '✓ คัดลอกแล้ว'; btn.style.color = '#2e7d32'; }
            };
            if (navigator.clipboard) {
              navigator.clipboard.writeText(draftRef).then(finish).catch(doCopy);
            } else {
              doCopy();
              finish();
            }
          });
        },
      });

      navigate(`/orderlist?tab=DRAFT&highlight=${encodeURIComponent(draftRef)}`);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err.response?.data?.error || err.message,
      });
    }
  };



  // ขั้นตอนหลังบันทึกใบเสนอราคา: popup "สำเร็จ" ให้เลือกส่งเป็นไฟล์ PDF หรือรูปภาพ
  // simulate = โหมดจำลองของ dev — ใช้บิลที่มีอยู่แล้ว ไม่บันทึก ไม่ล้างตะกร้า
  const showQTDone = async (doc_no, { simulate = false } = {}) => {
    const title = isQTEdit ? "บันทึกการแก้ไขสำเร็จ" : "สร้างใบเสนอราคาสำเร็จ";
    // ไม่เปิด PDF เอง — popup ให้เลือกส่งเป็นไฟล์ PDF หรือรูปภาพ
    await showQuotationDone(doc_no, QuotationDocument, { title: simulate ? `🧪 (จำลอง) ${title}` : title });
  };

  // 🧪 dev เท่านั้น: เล่นขั้นตอนหลังกดเสนอราคากับบิลที่มีอยู่แล้ว (DB ใช้ร่วมกับของจริง กดเสนอราคาจริงจะได้ QT จริง)
  const handleSimulateQTDone = async () => {
    const { value: docNo } = await Swal.fire({
      title: '🧪 จำลองหลังกดเสนอราคา',
      text: 'ใส่เลขใบเสนอราคาที่มีอยู่แล้ว — จะไม่บันทึกและไม่ล้างตะกร้า',
      input: 'text',
      inputValue: localStorage.getItem('ccs_simulate_qt') || '',
      inputPlaceholder: 'เช่น QT6910-0947',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'จำลอง',
      confirmButtonColor: '#1976d2',
      cancelButtonText: 'ยกเลิก',
      cancelButtonColor: '#757575',
      inputValidator: (v) => (!v?.trim() ? 'กรุณาใส่เลขใบเสนอราคา' : undefined),
    });
    if (!docNo) return;
    const doc_no = docNo.trim().toUpperCase();
    try { localStorage.setItem('ccs_simulate_qt', doc_no); } catch (e) { /* ignore */ }
    // หน่วงเวลาแทนการรอ /api/addQT — ให้ PDF เปิดหลัง await เหมือนของจริง (iPhone จะบล็อกหน้าต่างแบบเดียวกัน)
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1500));
    setIsSubmitting(false);
    await showQTDone(doc_no, { simulate: true });
  };

  // ฟังก์ชันสำหรับ "เสนอราคา"
  const handleOfferPrice = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try { await _handleOfferPriceInner(); } finally { setIsSubmitting(false); }
  };
  const _handleOfferPriceInner = async () => {
    if (!customerId) {
      Swal.fire({ icon: 'error', title: 'กรุณาเลือกลูกค้า', confirmButtonText: 'ตกลง', confirmButtonColor: '#1976d2' });
      return;
    }
    if (!cart.length) {
      Swal.fire({ icon: 'error', title: 'กรุณาเพิ่มสินค้าในตะกร้า', confirmButtonText: 'ตกลง', confirmButtonColor: '#1976d2' });
      return;
    }
    if (!invoiceDate) {
      Swal.fire({ icon: 'error', title: 'กรุณาเลือกวันที่บิล', confirmButtonText: 'ตกลง', confirmButtonColor: '#1976d2' });
      return;
    }
    if (!selectedSalesperson) {
      Swal.fire({ icon: 'error', title: 'กรุณาเลือกพนักงานขาย', confirmButtonText: 'ตกลง', confirmButtonColor: '#1976d2' });
      return;
    }
    if (shippingType === 'รับเอง' && !pickupBranch) {
      Swal.fire({ icon: 'error', title: 'กรุณาเลือกสาขาที่รับสินค้า', confirmButtonText: 'ตกลง', confirmButtonColor: '#1976d2' });
      return;
    }

    // จัดส่งแต่ยังไม่ระบุสถานที่ส่ง → อนุญาตให้สร้าง QT ได้ แต่แสดงคำเตือนใน dialog ยืนยัน
    const noDeliveryLocation = shippingType === 'จัดส่ง' && !selectedLocation;
    const summaryText = `ลูกค้า: ${customer}  จำนวนเงิน: ${formatNumber(cart.reduce((a, i) => a + Number(i.qty) * Number(i.price), 0) + shippingForTotal(shippingType, shippingCost) - Number(discount))} บาท`;

    const result = await Swal.fire({
      title: "ยืนยันการสร้างใบเสนอราคา?",
      html: `
        <div>${summaryText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        ${noDeliveryLocation ? '<div style="margin-top:10px;color:#ed6c02;font-weight:600">⚠ ยังไม่ได้ระบุสถานที่ส่ง</div>' : ''}
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2e7d32",
      cancelButtonColor: "#757575",
      confirmButtonText: "สร้างใบเสนอราคา",
      cancelButtonText: "ยกเลิก",
    });

    if (!result.isConfirmed) return;

    try {
      // Apply promos one final time before confirming
      let finalEffectivePromoDiscount = effectivePromoDiscount;
      if (cartGuid) {
        try {
          // Carry forward whatever code is currently applied so a code-gated
          // promo isn't dropped by this final re-evaluation.
          const appliedCode = appliedPromos.find(p => p.promo_code)?.promo_code;
          const { data: applyData } = await api.post('/api/promotions/apply',
            { cart_guid: cartGuid, promo_code: appliedCode }, { withCredentials: true }
          );
          setAppliedPromos(applyData.applied || []);
          setPromoDiscount(applyData.total_promo_discount || 0);
          finalEffectivePromoDiscount = computeEffectivePromoDiscount(applyData.applied || []);
        } catch (e) { /* non-blocking */ }
      }

      const { data } = await api.post(
        "/api/addQT",
        {
          remark,
          checkout_request_id: checkoutRequestId.current,
          effective_promo_discount: finalEffectivePromoDiscount,
        },
        { withCredentials: true }
      );
      const { doc_no } = data;
      // F7: Reset immediately after success so re-opened checkout gets a fresh UUID
      checkoutRequestId.current = generateUUID();

      if (data.dryRun) {
        await previewPDF(doc_no, QuotationDocument, data.previewData);
      } else {
        await showQTDone(doc_no);

        await unlockBill();
        await deleteCart();
        navigate(`/orderlist?highlight=${encodeURIComponent(doc_no)}`);
      }


    } catch (err) {
      if (err.response) {
        const msg = err.response.data.error || err.response.statusText;
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: msg,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: err.message,
        });
      }
    }
  };

  // สร้าง row สำหรับค่าขนส่ง (shipping row)
  const shippingRow = {
    rowIndex: updatedRows.length + 1,
    item_code: "SHIPPING_FEE",
    item_name: "ค่าบริการขนส่ง",
    qty: 1,
    unit_code: "ครั้ง",
    price: shippingCost,
    sum_amount: shippingCost,
    isInStock: true,
    roworder: -1,
  };

  // แจ้งเตือนเมื่อค่าส่งตอนนี้ต่างจากบิลล่าสุดที่ส่งไปที่อยู่เดียวกัน (ไม่มีประวัติ = ไม่แจ้ง)
  const latestInvoiceDiffers =
    latestInvoiceShippingCost !== null && Number(shippingCost || 0) !== latestInvoiceShippingCost;

  // ถ้าเป็นกรณีจัดส่งและกด "เพิ่มค่าขนส่ง" จึงจะแสดง shipping row
  const showShippingRow =
    shippingType === "จัดส่ง" && addShipping === "yes";

  // finalRows จะผสมกันระหว่าง updatedRows (cart items) กับ shippingRow (ถ้ามี)
  const finalRows = showShippingRow
    ? [...updatedRows, shippingRow]
    : updatedRows;

  // คำนวณยอดรวม (ใช้ใน summary ด้านล่าง)
  const {
    totalAmount, totalAfterDiscount, totalBeforeVat, vat, totalIncludingVat,
  } = computeCheckoutTotals({
    cart, shippingType, shippingCost, discount,
    promoDiscount: effectivePromoDiscount, loyaltyDiscount, taxType,
  });


  return (
    <>
      {isMobileLayout && (
        <Tabs
          value={mobileTab}
          onChange={(_, v) => setMobileTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#fafafa',
            flexShrink: 0,
            '& .MuiTab-root': { minHeight: 44, fontSize: 13, fontWeight: 600, py: 0 },
          }}
        >
          <Tab label={`รายการ (${updatedRows.filter(r => r.roworder !== -1).length})`} />
          <Tab label="รายละเอียด" />
        </Tabs>
      )}
      <div
        className="cart-container"
        style={isMobileLayout ? { padding: 0, gap: 0, '--cart-tabs-h': '44px' } : {}}
      >
        {/* ฝั่งซ้าย: รายการสินค้า + Summary */}
        <div className="cart-left" style={isMobileLayout ? { display: mobileTab === 0 ? 'flex' : 'none' } : {}}>
        <div className="cart-left-top">
          <div className="cart-table">
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              {/* Title bar */}
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: '8px 12px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>รายการสินค้า</Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {updatedRows.filter(r => r.roworder !== -1).length} รายการ
                  {totalWeight > 0 && (
                    <>
                      <Box component="span" sx={{ mx: 1.2 }}>—</Box>
                      {totalWeight.toLocaleString('en-US', {minimumFractionDigits: 2,maximumFractionDigits: 2})} กก.
                    </>
                  )}
                </Typography>
              </Box>

              {/* Scrollable table area: header + rows scroll together */}
              <Box ref={scrollBoxRef} sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
              {/* มือถือ: ไม่บังคับกว้าง 620px (เลื่อนซ้าย-ขวาแล้วชื่อสินค้าตกขอบ) — แถวจัดเป็น 2 บรรทัดแทน */}
              <Box sx={{ minWidth: { xs: 0, sm: 620 } }}>

              {/* Column headers */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', bgcolor: '#f5f5f5', px: '12px', py: '6px', borderBottom: '1px solid #e0e0e0', gap: '8px', position: 'sticky', top: 0, zIndex: 1 }}>
                <Box sx={{ width: 32, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary', width: 80, flexShrink: 0 }}>รหัส</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', flex: 1, minWidth: 0 }}>รายการสินค้า</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', width: 70, textAlign: 'right', flexShrink: 0 }}>จำนวน</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', width: 75, flexShrink: 0, pl: '4px' }}>หน่วย</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', width: 90, textAlign: 'right', flexShrink: 0 }}>ราคา/หน่วย</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', width: 80, textAlign: 'right', flexShrink: 0 }}>รวม</Typography>
                <Box sx={{ width: 32, flexShrink: 0 }} />
              </Box>

              {/* List */}
                {finalRows.map((row) => {
                  const isShipping = row.roworder === -1;
                  const isSelected = selectedRowId === row.roworder;
                  const totalRowWeight = (row.weight || 0) * (row.qty || 0);
                  const sumAmount = Number(row.qty) * Number(row.price);
                  const lookupKey = `${row.item_code}_${row.unit_code}`;
                  const original = originalPrices[lookupKey];
                  const hasPriceDiff = original && Number(row.price) !== Number(original);
                  const lineDiscount = discountByItemCode[row.item_code] || 0;
                  const discountedUnitPrice = Math.max(0, Number(row.price) - lineDiscount / Number(row.qty));
                  const hasPromoDiscount = !isShipping && lineDiscount > 0 && !hasPriceDiff && !manualPriceEdits.has(row.roworder);
                  const { units: unitList = [] } = unitOptions[row.item_code] || {};
                  const canDrag = !isShipping && editingCell?.roworder !== row.roworder;
                  const isDragging = draggingId === row.roworder;
                  const isDropTarget = draggingId != null && !isDragging && dragOverId === row.roworder;
                  // Line shows where the dragged row will land: below the target when moving down, above when moving up
                  const dropBelow = isDropTarget
                    && finalRows.findIndex(r => r.roworder === draggingId) < finalRows.findIndex(r => r.roworder === row.roworder);
                  // มือถือ (xs): บรรทัด 1 = ลาก | ชื่อสินค้า | ลบ, บรรทัด 2 = จำนวน หน่วย ราคา รวม (เยื้องใต้ชื่อ)
                  const line2 = { flexShrink: 0, order: { xs: 2, sm: 0 } };
                  const colQty = { ...line2, width: { xs: 56, sm: 70 }, ml: { xs: '40px', sm: 0 } };
                  const colUnit = { ...line2, width: { xs: 60, sm: 75 } };
                  const colPrice = { ...line2, width: { xs: 'auto', sm: 90 }, flex: { xs: 1, sm: 'none' }, minWidth: 0 };
                  const colTotal = { ...line2, width: { xs: 84, sm: 80 } };

                  return (
                    <Box
                      key={row.roworder}
                      data-roworder={row.roworder}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isShipping) setSelectedRowId(row.roworder);
                      }}
                      sx={{
                        display: 'flex',
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        alignItems: 'center',
                        gap: '8px',
                        rowGap: { xs: '4px', sm: '8px' },
                        px: '12px',
                        py: { xs: '8px', sm: '6px' },
                        borderBottom: '0.5px solid #e0e0e0',
                        cursor: isShipping ? 'default' : 'pointer',
                        bgcolor: isSelected ? 'action.selected' : isDropTarget ? 'action.hover' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'action.selected' : isShipping ? 'transparent' : 'action.hover' },
                        '&:last-child': { borderBottom: 'none' },
                        opacity: isDragging ? 0.5 : 1,
                        boxShadow: isDropTarget ? (theme) => `inset 0 ${dropBelow ? -2 : 2}px 0 ${theme.palette.primary.main}` : 'none',
                        userSelect: 'none',
                      }}
                    >
                      {/* Drag handle — 32px column; pointer events so touch devices can reorder too */}
                      <Box
                        onPointerDown={canDrag ? (e) => handleHandlePointerDown(e, row.roworder) : undefined}
                        onPointerMove={handleHandlePointerMove}
                        onPointerUp={handleHandlePointerUp}
                        onPointerCancel={resetDrag}
                        onClick={(e) => { if (canDrag) e.stopPropagation(); }}
                        sx={{
                          width: 32,
                          flexShrink: 0,
                          alignSelf: 'stretch',
                          my: '-6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          touchAction: canDrag ? 'none' : 'auto',
                          cursor: canDrag ? (isDragging ? 'grabbing' : 'grab') : 'default',
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: 'text.secondary', fontSize: 18, opacity: isShipping ? 0 : 1 }} />
                      </Box>

                      {/* รหัสสินค้า — 80px */}
                      <Typography sx={{
                        display: { xs: 'none', sm: 'block' },
                        fontSize: 12,
                        color: 'text.secondary',
                        width: 90,
                        flexShrink: 0,
                        whiteSpace: 'normal',
                        wordBreak: 'break-all',
                      }}>
                        {isShipping ? 'ขนส่ง' : row.item_code}
                      </Typography>

                      {/* ชื่อสินค้า + น้ำหนัก — flex 1, stock status via tooltip + colour */}
                      {editingCell?.roworder === row.roworder && editingCell?.field === 'item_name' ? (
                        <Box sx={{ flex: { xs: '1 1 calc(100% - 80px)', sm: 1 }, minWidth: 0 }}>
                          <TextField
                            fullWidth
                            autoFocus
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleCellBlur(row, 'item_name', editValue)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCellBlur(row, 'item_name', editValue); }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ '& input': { fontSize: 14, py: '3px', px: '4px' } }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{ flex: { xs: '1 1 calc(100% - 80px)', sm: 1 }, minWidth: 0 }}
                          onDoubleClick={(e) => { if (!isShipping) { e.stopPropagation(); setEditingCell({ roworder: row.roworder, field: 'item_name' }); setEditValue(String(row.item_name ?? '')); } }}
                        >
                          <Box sx={{ lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, lineHeight: 'inherit', color: row.isInStock === false ? '#d32f2f' : (row.isLocalShort ? '#ed6c02' : 'text.primary') }}>
                              {row.item_name}
                            </Typography>
                            {' '}
                            <Tooltip
                              arrow
                              placement="top"
                              title={renderStockTooltip(row)}
                              open={stockTipId === row.roworder}
                              onOpen={() => setStockTipId(row.roworder)}
                              onClose={() => setStockTipId(null)}
                              disableTouchListener
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: '#1e1e1e',
                                    '& .MuiTooltip-arrow': { color: '#1e1e1e' },
                                    px: 1,
                                    py: 0.75,
                                    borderRadius: 1,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                                    maxWidth: 'calc(100vw - 32px)',
                                  }
                                },
                                arrow: { sx: { fontSize: 10 } }
                              }}
                            >
                              {/* Tap opens on touch devices; ::before enlarges the 6px dot's hit area without affecting layout */}
                              <Box
                                component="span"
                                data-stock-tip
                                onClick={(e) => { e.stopPropagation(); setStockTipId(row.roworder); }}
                                sx={{
                                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                                  bgcolor: row.isInStock == null ? '#9e9e9e' : row.isLocalShort ? '#ed6c02' : row.isInStock ? '#66bb6a' : '#ef5350',
                                  verticalAlign: 'middle', cursor: 'pointer', flexShrink: 0,
                                  position: 'relative',
                                  '&::before': { content: '""', position: 'absolute', inset: -10 },
                                }}
                              />
                            </Tooltip>
                          </Box>
                          {!isShipping && (
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.2 }}>
                              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{row.item_code} · </Box>
                              {formatNumber(totalRowWeight)} กก.
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* จำนวน — 70px */}
                      {editingCell?.roworder === row.roworder && editingCell?.field === 'qty' ? (
                        <TextField
                          autoFocus
                          value={editValue}
                          size="small"
                          type="number"
                          inputProps={{ inputMode: 'decimal' }}
                          sx={{ ...colQty, '& input': { fontSize: 14, textAlign: 'right', py: '3px', px: '4px' } }}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCellBlur(row, 'qty', editValue); }}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={() => handleCellBlur(row, 'qty', editValue)}
                        />
                      ) : (
                        <Typography
                          sx={{ ...colQty, fontSize: 14, textAlign: 'right' }}
                          onDoubleClick={(e) => { if (!isShipping) { e.stopPropagation(); setEditingCell({ roworder: row.roworder, field: 'qty' }); setEditValue(String(row.qty)); } }}
                        >
                          {Number(row.qty) % 1 === 0 ? Number(row.qty).toLocaleString('en-US') : formatNumber(row.qty)}
                        </Typography>
                      )}

                      {/* หน่วย — 75px */}
                      {editingCell?.roworder === row.roworder && editingCell?.field === 'unit_code' && unitList.length > 0 ? (
                        <Select
                          value={editValue}
                          size="small"
                          sx={{ ...colUnit, fontSize: 13, '& .MuiSelect-select': { py: '3px', px: '4px' } }}
                          onChange={(e) => {
                            const newVal = e.target.value;
                            setEditingCell(null);
                            const oldRow = updatedRows.find(r => r.roworder === row.roworder);
                            if (oldRow && newVal !== oldRow.unit_code) {
                              processRowUpdate({ ...oldRow, unit_code: newVal }, oldRow);
                            }
                          }}
                          onBlur={() => setEditingCell(null)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {unitList.map((u) => {
                            const raw = u.stand_value / u.divide_value;
                            return (
                              <MenuItem key={u.code} value={u.code} sx={{ fontSize: 13 }}>
                                {u.code}{raw > 1 ? ` ×${raw}` : ''}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      ) : (
                        <Typography
                          sx={{ ...colUnit, fontSize: 14, pl: '4px' }}
                          onDoubleClick={(e) => { if (!isShipping && unitList.length > 0) { e.stopPropagation(); setEditingCell({ roworder: row.roworder, field: 'unit_code' }); setEditValue(row.unit_code || ''); } }}
                        >
                          {row.unit_code}
                        </Typography>
                      )}

                      {/* ราคา/หน่วย — 90px */}
                      {editingCell?.roworder === row.roworder && editingCell?.field === 'price' ? (
                        <TextField
                          autoFocus
                          value={editValue}
                          size="small"
                          type="number"
                          inputProps={{ inputMode: 'decimal' }}
                          sx={{ ...colPrice, '& input': { fontSize: 14, fontWeight: 500, textAlign: 'right', py: '3px', px: '4px' } }}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleCellBlur(row, 'price', editValue); }}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={() => handleCellBlur(row, 'price', editValue)}
                        />
                      ) : (
                        <Tooltip
                          arrow
                          placement="top"
                          disableHoverListener={!hasPriceDiff}
                          title={hasPriceDiff ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>ราคาเดิม</Typography>
                              <Typography sx={{ fontSize: 12, color: '#fff', fontWeight: 500 }}>{formatNumber(original)}</Typography>
                            </Box>
                          ) : ''}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: '#1e1e1e',
                                '& .MuiTooltip-arrow': { color: '#1e1e1e' },
                                px: 1.5,
                                py: 1,
                                borderRadius: 1,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                                maxWidth: 'none',
                              }
                            },
                            arrow: { sx: { fontSize: 10 } }
                          }}
                        >
                          {hasPromoDiscount ? (
                            <Box
                              sx={{ ...colPrice, textAlign: 'right' }}
                              onDoubleClick={(e) => { if (!isShipping) { e.stopPropagation(); setEditingCell({ roworder: row.roworder, field: 'price' }); setEditValue(String(row.price)); } }}
                            >
                              <Typography sx={{ fontSize: 11, color: hasPriceDiff ? 'warning.dark' : 'text.disabled', textDecoration: 'line-through', lineHeight: 1.2 }}>
                                {formatNumber(row.price)}
                              </Typography>
                              <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#2e7d32', lineHeight: 1.2 }}>
                                {formatNumber(discountedUnitPrice)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                ...colPrice,
                                fontSize: 14,
                                fontWeight: 500,
                                textAlign: 'right',
                                color: hasPriceDiff ? 'warning.dark' : 'text.primary',
                              }}
                              onDoubleClick={(e) => { if (!isShipping) { e.stopPropagation(); setEditingCell({ roworder: row.roworder, field: 'price' }); setEditValue(String(row.price)); } }}
                            >
                              {formatNumber(row.price)}
                            </Typography>
                          )}
                        </Tooltip>
                      )}

                      {/* รวม — 80px */}
                      {hasPromoDiscount ? (
                        <Box sx={{ ...colTotal, textAlign: 'right' }}>
                          <Typography sx={{ fontSize: 11, color: 'text.disabled', textDecoration: 'line-through', lineHeight: 1.2 }}>
                            {formatNumber(sumAmount)}
                          </Typography>
                          <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#2e7d32', lineHeight: 1.2 }}>
                            {formatNumber(Math.max(0, sumAmount - lineDiscount))}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ ...colTotal, fontSize: 14, fontWeight: 600, textAlign: 'right', color: 'text.primary' }}>
                          {formatNumber(sumAmount)}
                        </Typography>
                      )}

                      {/* ปุ่มลบ — 32px */}
                      <Box sx={{ width: 32, flexShrink: 0, order: { xs: 1, sm: 0 }, display: 'flex', justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          sx={{ p: 0.5 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isShipping) return;
                            Swal.fire({
                              title: 'ลบรายการ?',
                              text: row.item_name,
                              icon: 'warning',
                              showCancelButton: true,
                              confirmButtonColor: '#d32f2f',
                              cancelButtonColor: '#757575',
                              confirmButtonText: 'ลบ',
                              cancelButtonText: 'ยกเลิก',
                            }).then(({ isConfirmed }) => {
                              if (isConfirmed) deleteItemFromCart(row.roworder);
                            });
                          }}
                          disabled={isShipping}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              </Box>
            </Box>
          </div>
        </div>
        <div className="cart-left-bottom">
          {/* โค้ดโปรโมชัน — ด้านซ้ายของ summary (ซ่อนไว้ก่อน ยังไม่เปิดใช้: SHOW_PROMO_CODE) */}
          {SHOW_PROMO_CODE && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, mb: 0.5 }}>โค้ดโปรโมชัน</Typography>
            <Box display="flex" gap={1} alignItems="flex-start">
              <TextField
                size="small"
                placeholder="กรอกโค้ดโปรโมชัน"
                value={promoCode}
                onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleApplyPromoCode()}
                error={!!promoError}
                helperText={promoError}
                inputProps={{ maxLength: 80 }}
                sx={{ width: { xs: 'auto', md: 180 }, flex: { xs: 1, md: 'none' }, '& input': { fontSize: 13 } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleApplyPromoCode}
                disabled={promoLoading || !promoCode.trim()}
                sx={{ whiteSpace: 'nowrap', fontSize: 12, height: 36 }}
              >
                {promoLoading ? <CircularProgress size={14} /> : 'ใช้โค้ด'}
              </Button>
            </Box>
          </Box>
          )}

          {/* Summary box */}
          <Box sx={{ bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1, ml: 'auto', width: { xs: '100%', md: 'fit-content' }, minWidth: { xs: 0, md: 360 }, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ px: 2, py: 1, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>สรุปยอดรวม</Typography>
            </Box>

            <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* ราคารวมสินค้า */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ flex: 1, fontSize: 15, color: 'text.secondary', whiteSpace: 'nowrap' }}>ราคารวมสินค้า</Typography>
                <Typography sx={{ minWidth: 120, textAlign: 'right', fontSize: 15 }}>{formatNumber(totalAmount)}</Typography>
                <Typography sx={{ width: 32, textAlign: 'right', fontSize: 14, color: 'text.secondary' }}>บาท</Typography>
              </Box>

              {/* ส่วนลด */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ flex: 1, fontSize: 15, color: 'text.secondary', whiteSpace: 'nowrap' }}>ส่วนลด</Typography>
                <Box sx={{ minWidth: 120, display: 'flex', justifyContent: 'flex-end' }}>
                  <TextField
                    type="number"
                    value={discount}
                    onChange={handleDiscountChange}
                    onBlur={handleDiscountBlur}
                    disabled={!canEditDiscount}
                    size="small"
                    sx={{
                      width: 120,
                      '& .MuiInputBase-root': { height: 32 },
                      '& input': {
                        textAlign: 'right',
                        fontSize: 15,
                        py: 0,
                        '&::-webkit-outer-spin-button': { display: 'none' },
                        '&::-webkit-inner-spin-button': { display: 'none' },
                        MozAppearance: 'textfield',
                      },
                    }}
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Box>
                <Typography sx={{ width: 32, textAlign: 'right', fontSize: 14, color: 'text.secondary' }}>บาท</Typography>
              </Box>

              {/* ── โปรโมชัน ── */}
              {appliedPromos.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                  {appliedPromos.map(p => {
                    const excludedForPromo = (p.matched_item_codes || []).reduce((s, e) =>
                      manualEditItemCodes.has(e.item_code) ? s + (e.line_discount || 0) : s, 0);
                    const adjustedAmount = Math.max(0, Number(p.discount_amount) - excludedForPromo);
                    if (!p.is_free_gift && adjustedAmount === 0) return null;
                    return (
                    <Box key={p.id} display="flex" alignItems="center" gap={1}>
                      <Chip
                        size="small"
                        icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                        label={p.promotion_name}
                        color={p.is_free_gift ? 'warning' : 'success'}
                        variant="outlined"
                        onDelete={p.id ? () => handleRemovePromo(p.id) : undefined}
                        sx={{ maxWidth: 160, fontSize: 11 }}
                      />
                      {!p.is_free_gift && (
                        <>
                          <Typography sx={{ flex: 1, textAlign: 'right', fontSize: 13, color: '#2e7d32' }}>
                            -{formatNumber(adjustedAmount)}
                          </Typography>
                          <Typography sx={{ width: 32, textAlign: 'right', fontSize: 12, color: '#2e7d32' }}>บาท</Typography>
                        </>
                      )}
                      {p.is_free_gift && (
                        <Typography sx={{ flex: 1, fontSize: 12, color: '#ed6c02' }}>ของแถม</Typography>
                      )}
                    </Box>
                  );
                  })}
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ borderTop: '1px dashed #e0e0e0', my: 0.25 }} />

              {/* ราคาหลังส่วนลด */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ flex: 1, fontSize: 15, color: 'text.secondary', whiteSpace: 'nowrap' }}>ราคาหลังส่วนลด</Typography>
                <Typography sx={{ minWidth: 120, textAlign: 'right', fontSize: 15, fontWeight: 500 }}>{formatNumber(totalAfterDiscount)}</Typography>
                <Typography sx={{ width: 32, textAlign: 'right', fontSize: 14, color: 'text.secondary' }}>บาท</Typography>
              </Box>

              {/* ภาษี */}
              {taxType !== 'novat' && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ flex: 1, fontSize: 15, color: 'text.secondary', whiteSpace: 'nowrap' }}>ก่อนภาษี</Typography>
                  <Typography sx={{ minWidth: 120, textAlign: 'right', fontSize: 15 }}>{formatNumber(totalBeforeVat)}</Typography>
                  <Typography sx={{ width: 32, textAlign: 'right', fontSize: 14, color: 'text.secondary' }}>บาท</Typography>
                </Box>
              )}
              {taxType !== 'novat' && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ flex: 1, fontSize: 15, color: 'text.secondary', whiteSpace: 'nowrap' }}>ภาษีมูลค่าเพิ่ม 7%</Typography>
                  <Typography sx={{ minWidth: 120, textAlign: 'right', fontSize: 15 }}>{formatNumber(vat)}</Typography>
                  <Typography sx={{ width: 32, textAlign: 'right', fontSize: 14, color: 'text.secondary' }}>บาท</Typography>
                </Box>
              )}
            </Box>

            {/* Grand total */}
            <Box display="flex" alignItems="center" gap={1} sx={{ px: 2, py: 1.25, borderTop: '2px solid #e0e0e0', bgcolor: '#fff8f8' }}>
              <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 600, color: '#c62828', whiteSpace: 'nowrap' }}>รวมทั้งสิ้น</Typography>
              <Typography sx={{ minWidth: 120, textAlign: 'right', fontSize: 24, fontWeight: 700, color: '#c62828', lineHeight: 1 }}>{formatNumber(totalIncludingVat)}</Typography>
              <Typography sx={{ width: 32, textAlign: 'right', fontSize: 15, fontWeight: 600, color: '#c62828' }}>บาท</Typography>
            </Box>
          </Box>
        </div>
      </div>

      {/* ฝั่งขวา: รายละเอียดและ Actions */}
      <div className="cart-right" style={isMobileLayout ? { display: mobileTab === 1 ? 'flex' : 'none', width: '100%' } : {}}>
        <Box sx={{
          '& .MuiInputBase-input': { fontSize: '14px' },
          '& .MuiSelect-select': { fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>

          {/* Scrollable fields card */}
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px', p: '8px 12px' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              รายละเอียด{' '}
              {isDraftEdit || isEditing
                ? `(แก้ไขบิล: ${currentOrderNo})`
                : `(สร้างบิลใหม่)`}
            </Typography>


            {/* วันที่บิล + พนักงานขาย */}
            <Box display="flex" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>วันที่บิล</Typography>
                <LocalizationProvider dateAdapter={yearAdapter} adapterLocale={thDate}>
                  <DatePicker
                    value={invoiceDate}
                    onChange={handleInvoiceDateChange}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>พนักงานขาย</Typography>
                <Select
                  size="small"
                  fullWidth
                  value={selectedSalesperson}
                  onChange={handleSalePersonChange}
                  disabled={!canChangeSalesperson}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                >
                  {sortedSalespersons.map((sale) => (
                    <MenuItem
                      key={sale.code}
                      value={sale.code}
                      disabled={isCreditBill && !sale.groups?.some(g => g.group_code === 'แผนกขายเครดิต')}
                    >
                      {sale.name_1}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  หัวบิลออกสาขา: {branch || "ไม่มีข้อมูลสาขา"}
                </Typography>
              </Box>
            </Box>


            {/* ชื่อลูกค้า + ปุ่มเพิ่มลูกค้า */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>ชื่อลูกค้า</Typography>
              <Box display="flex" gap={1} alignItems="flex-start">
                <Autocomplete
                  freeSolo={false}
                  options={customers.filter(Boolean)}
                  size="small"
                  sx={{ flex: 1 }}
                  value={
                    customer
                      ? customers.find((cust) => cust.code === customerId) || { code: customerId, name_1: customer }
                      : null
                  }
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason !== 'reset') {
                      setInputValue(newInputValue);
                      handleSearch(event, newInputValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      handleCustomerChange(event, newValue);
                    } else {
                      setInputValue("");
                      setCustomer("");
                    }
                  }}
                  getOptionLabel={(option) => typeof option === 'string' ? option : (option?.name_1 ?? '')}
                  isOptionEqualToValue={(option, value) => option?.code === value?.code}
                  filterOptions={(options, { inputValue }) => {
                    const q = (inputValue ?? '').toLowerCase();
                    const digits = q.replace(/\D/g, '');
                    return options.filter(o =>
                      o?.is_fuzzy ||
                      o?.name_1?.toLowerCase().includes(q) ||
                      (digits.length >= 3 && (o?.telephone ?? '').replace(/\D/g, '').includes(digits))
                    );
                  }}
                  loading={loading}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography component="span" sx={{ color: 'grey.500', fontWeight: 300, fontSize: '0.75rem' }}>
                        {option?.code ?? ''}
                      </Typography>
                      <Typography component="span" sx={{ ml: 1 }}>
                        {option?.name_1 ?? ''}
                      </Typography>
                      {option?.telephone && (
                        <Typography component="span" sx={{ ml: 1, color: 'grey.500', fontSize: '0.75rem' }}>
                          ({option.telephone})
                        </Typography>
                      )}
                      {option?.is_fuzzy && (
                        <Chip label="ใกล้เคียง" size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      onBlur={() => {
                        const match = customers.find(c => c.name_1 === inputValue);
                        if (!match) {
                          setInputValue(customer);
                          Swal.fire({
                          icon: 'warning',
                          title: 'กรุณาเลือกลูกค้าจากรายการ',
                          confirmButtonText: 'ตกลง',
                          confirmButtonColor: '#1976d2',
                        });
                        }
                      }}
                      helperText={
                        email ? <span style={{ color: "red" }}>E-Tax ({email})</span> : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ width: 80, flexShrink: 0, height: 40 }}
                  startIcon={<AddIcon />}
                  onClick={() => setOpenCustomerDialog(true)}
                >
                  เพิ่ม
                </Button>
              </Box>
              <InlineCustomerNote custCode={customerId} />
            </Box>

            <NewCustomerDialog
              open={openCustomerDialog}
              onClose={() => setOpenCustomerDialog(false)}
              onSuccess={(newCustomer) => {
                setCustomers(prev => [newCustomer, ...prev]);
                handleCustomerChange(null, newCustomer);
              }}
            />

            {/* ประเภทบิล + วันเครดิต */}
            <Box display="flex" gap={1}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>ประเภทบิล</Typography>
                <Select size="small" fullWidth value={arType} onChange={handleArTypeChange} disabled={!isCreditGroup}>
                  <MenuItem value="เงินสด">เงินสด</MenuItem>
                  <MenuItem value="เครดิต">เครดิต</MenuItem>
                </Select>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', width: 80 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>วันเครดิต</Typography>
                <TextField
                  type="number"
                  inputProps={{ inputMode: 'numeric' }}
                  value={creditDay}
                  onChange={handleCreditDayChange}
                  size="small"
                  fullWidth
                  disabled={arType !== 'เครดิต'}
                />
              </Box>
            </Box>

            {/* ราคา + ช่องติ้กภาษี */}
            <Box display="flex" gap={1} alignItems="flex-end">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>ราคา</Typography>
                <Select size="small" fullWidth value={priceType} onChange={handlePriceTypeChange} disabled={!canChangePriceLevel}>
                  <MenuItem value="สด">สด</MenuItem>
                  <MenuItem value="7%">7%</MenuItem>
                  <MenuItem value="10%">10%</MenuItem>
                  <MenuItem value="12%">12%</MenuItem>
                  <MenuItem value="15%">15%</MenuItem>
                </Select>
              </Box>
              <Box sx={{ width: 80, display: 'flex', alignItems: 'center', gap: '6px', pl: 1, pb: 1 }}>
                <Checkbox
                  size="small"
                  sx={{ padding: 0 }}
                  checked={taxType === "vat"}
                  onChange={(e) => handleTaxTypeChange(e.target.checked ? "vat" : "novat")}
                />
                <Typography sx={{ fontSize: 13 }}>ภาษี</Typography>
              </Box>
            </Box>

            {/* รับ/ส่งวันที่ + การจัดส่ง */}
            <Box display="flex" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>รับ/ส่งวันที่</Typography>
                <LocalizationProvider dateAdapter={yearAdapter} adapterLocale={thDate}>
                  <DatePicker
                    value={deliveryDate}
                    onChange={handleDeliveryDateChange}
                    format="dddd DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>การจัดส่ง</Typography>
                <Select size="small" fullWidth value={shippingType} onChange={handleShippingTypeChange}>
                  <MenuItem value="รับเอง">รับเอง</MenuItem>
                  <MenuItem value="จัดส่ง">จัดส่ง</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* กรณี "จัดส่ง" */}
            {shippingType === "จัดส่ง" && (
              <>
                {/* สถานที่ส่ง + ปุ่มเพิ่มที่อยู่ */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>สถานที่ส่ง</Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <Select
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      value={selectedLocation}
                      onChange={handleSelectedLocationChange}
                    >
                      <MenuItem value="">-- เลือกสถานที่ --</MenuItem>
                      {deliveryLocations.map((loc) => (
                        <MenuItem key={loc.roworder} value={loc.roworder}>
                          {loc.address}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ width: 80, flexShrink: 0, height: 36}}
                      startIcon={<AddIcon />}
                      onClick={() => setOpenMapDialog(true)}
                    >
                      เพิ่ม
                    </Button>
                  </Box>
                </Box>

                {/* ค่าขนส่ง + ช่องติ้กค่าขนส่ง */}
                <Box display="flex" gap={1} alignItems="flex-end">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>ค่าขนส่ง</Typography>
                    <TextField
                      type="number"
                      inputProps={{ inputMode: 'decimal' }}
                      size="small"
                      fullWidth
                      value={shippingCost}
                      disabled={addShipping !== "yes" || !selectedLocation}
                      onChange={handleShippingCostChange}
                      InputLabelProps={{ shrink: shippingCost !== null && shippingCost !== undefined }}
                    />
                  </Box>
                  <Box sx={{ width: 80, display: 'flex', alignItems: 'center', gap: '6px', pl: 1, pb: 1 }}>
                    <Checkbox
                      size="small"
                      sx={{ padding: 0 }}
                      checked={addShipping === "yes"}
                      disabled={!selectedLocation}
                      onChange={async (e) => {
                        const checked = e.target.checked;
                        setAddShipping(checked ? "yes" : "no");
                        setIsShippingCostManual(false);
                        if (checked && selectedLocation) {
                          const { cost } = await calculateShippingCost();
                          setShippingCost(cost);
                          updateCartHeaderField("shipping_cost", cost);
                        } else {
                          setShippingCost(0);
                          updateCartHeaderField("shipping_cost", 0);
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: 13 }}>เพิ่ม</Typography>
                  </Box>
                </Box>

                {(addShipping === "yes" && (shippingInfo || latestInvoiceDiffers)) && (
                  <Alert severity={isShippingEstimated ? "warning" : "info"} sx={{ fontSize: '12px', py: 0.5 }}>
                    {shippingInfo && <div>{shippingInfo}</div>}
                    {latestInvoiceDiffers && (
                      <div style={{ color: "red" }}>
                        ล่าสุดคิดค่าขนส่ง : {formatNumber(latestInvoiceShippingCost)} บาท ({latestInvoiceDate})
                      </div>
                    )}
                  </Alert>
                )}
              </>
            )}

            {/* กรณี "รับเอง" */}
            {shippingType === "รับเอง" && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>รับสาขา</Typography>
                <Select size="small" fullWidth value={pickupBranch} onChange={handlePickupBranchChange}>
                  {branchList.map((branch) => (
                    <MenuItem key={branch.code} value={branch.code}>
                      {branch.name_1}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            <NewDeliveryLocationDialog
              open={openMapDialog}
              onClose={() => setOpenMapDialog(false)}
              onSuccess={(newId) => {
                api
                  .post(`/api/getCustomerDeliveryLocations?customer_id=${customerId}`)
                  .then(res => {
                    setDeliveryLocations(res.data.locations || []);
                    handleSelectedLocationChange({ target: { value: newId } });
                  })
                  .catch(err => console.error(err));
              }}
              customerId={customerId}
            />

            {/* หมายเหตุ */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>หมายเหตุ</Typography>
              <TextField
                value={remark}
                fullWidth
                multiline
                rows={2}
                size="small"
                onChange={handleRemarkChange}
              />
            </Box>
          </Box>

          {/* Fixed action buttons */}
          <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px', p: '12px 16px 16px' }}>

            {/* Primary CTA */}
            {(!isEditing || isDraftEdit) && (
              <Button
                variant="contained"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  background: '#1b5e20', '&:hover': { background: '#2e7d32' },
                  height: 48, fontWeight: 700, borderRadius: '8px',
                  fontSize: '0.9rem', letterSpacing: 0.3,
                }}
                startIcon={<SendIcon />}
                onClick={handleOfferPrice}
              >
                {isSubmitting ? 'กำลังส่ง...' : 'เสนอราคา'}
              </Button>
            )}
            {isEditing && !isDraftEdit && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ height: 48, fontWeight: 700, borderRadius: '8px', fontSize: '0.9rem', letterSpacing: 0.3 }}
                startIcon={<SendIcon />}
                onClick={handleOfferPrice}
              >
                {isSubmitting ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
              </Button>
            )}
            {currentUser?.is_dev && (
              <Button
                variant="text"
                size="small"
                disabled={isSubmitting}
                onClick={handleSimulateQTDone}
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
              >
                🧪 จำลองหลังกดเสนอราคา (dev)
              </Button>
            )}

            {/* Secondary row: Draft + Destructive side-by-side */}
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="outlined"
                color="error"
                sx={{ flex: 1, height: 38, borderRadius: '8px', fontWeight: 500 }}
                startIcon={
                  (lockInfo || isQTEdit) ? <UndoIcon /> :
                  isDraftEdit ? <DeleteIcon /> :
                  <RestartAltIcon />
                }
                onClick={async () => {
                  if (lockInfo || isQTEdit) {
                    const result = await Swal.fire({
                      title: "ยืนยันยกเลิกการแก้ไข",
                      text: "บิลที่กำลังแก้ไขอยู่จะไม่ถูกบันทึกการแก้ไข",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d32f2f",
                      cancelButtonColor: "#757575",
                      confirmButtonText: "ยืนยันยกเลิก",
                      cancelButtonText: "ยกเลิก",
                    });
                    if (!result.isConfirmed) return;
                    await unlockBill();
                    await deleteCart();
                    resetCartState();
                    navigate('/orderlist');
                  } else if (isDraftEdit) {
                    const result = await Swal.fire({
                      title: "ลบ Draft?",
                      text: `Draft "${currentOrderNo}" จะถูกลบ`,
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d32f2f",
                      cancelButtonColor: "#757575",
                      confirmButtonText: "ลบ Draft",
                      cancelButtonText: "ยกเลิก",
                    });
                    if (!result.isConfirmed) return;
                    await deleteCart();
                    resetCartState();
                    navigate('/orderlist?tab=DRAFT');
                  } else {
                    const result = await Swal.fire({
                      title: "ล้างข้อมูลทั้งหมด?",
                      text: "ข้อมูลที่กรอกไว้ทั้งหมดจะหายไป และระบบจะพาคุณกลับไปหน้าสินค้า",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d32f2f",
                      cancelButtonColor: "#757575",
                      confirmButtonText: "ล้างข้อมูล",
                      cancelButtonText: "ยกเลิก",
                    });
                    if (!result.isConfirmed) return;
                    await deleteCart();
                    resetCartState();
                    navigate('/');
                  }
                }}
              >
                {(lockInfo || isQTEdit) ? "ยกเลิก" : (isDraftEdit ? "ลบ Draft" : "ล้างข้อมูล")}
              </Button>
              {(!isEditing || isDraftEdit) && (
                <Button
                  variant="outlined"
                  sx={{ flex: 1, height: 38, borderRadius: '8px', fontWeight: 500 }}
                  startIcon={<SaveOutlinedIcon />}
                  onClick={handleSaveDraft}
                >
                  Draft
                </Button>
              )}
              
            </Box>

          </Box>
        </Box>
      </div>

    </div>

      {snackbar && (
        <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </>
  );
}
